'use client';

import React, { useState, useRef, useEffect, useCallback, useContext, createContext } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import SearchHintDropdown, { BOOK_SEARCH_HINTS } from '@/components/ui/SearchHintDropdown';
import { getBooks, isPriceVisible, mapBookFromRow } from '@/lib/books';
import { ADMIN_ACCESS_CODE, saveAdminSession } from '@/lib/admin-auth';
import { Book } from '@/lib/types';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import dynamic from 'next/dynamic';

const TikTokLiveCheckoutModal = dynamic(() => import('@/app/cart/components/TikTokLiveCheckoutModal'), { ssr: false });

// ── Preorder Cart Context ──────────────────────────────────
export interface CartItem {
  book: Book;
  qty: number;
  soldOut?: boolean; // true if book became sold out after being added
}

interface CartContextValue {
  items: CartItem[];
  addItem: (book: Book) => void;
  removeItem: (bookId: string) => void;
  updateQty: (bookId: string, qty: number) => void;
  clearCart: () => void;
  total: number;
  refreshCartStock: () => Promise<void>;
}

export const CartContext = React.createContext<CartContextValue>({
  items: [],
  addItem: () => {},
  removeItem: () => {},
  updateQty: () => {},
  clearCart: () => {},
  total: 0,
  refreshCartStock: async () => {},
});

const CART_STORAGE_KEY = 'ds_cart_items';

// ── DB cart helpers ────────────────────────────────────────
async function saveCartToDB(customerId: string, items: CartItem[]) {
  try {
    const { createClient } = await import('@/lib/supabase/client');
    const supabase = createClient();
    // Delete all existing rows for this customer, then re-insert
    await supabase
      .from('customer_cart_items')
      .delete()
      .eq('customer_id', customerId);
    if (items.length === 0) return;
    const rows = items.map(i => ({
      customer_id: customerId,
      book_id: i.book.id,
      qty: i.qty,
    }));
    await supabase.from('customer_cart_items').insert(rows);
  } catch { /* ignore — localStorage is the fallback */ }
}

async function loadCartFromDB(customerId: string): Promise<{ bookId: string; qty: number }[] | null> {
  try {
    const { createClient } = await import('@/lib/supabase/client');
    const supabase = createClient();
    const { data } = await supabase
      .from('customer_cart_items')
      .select('book_id, qty')
      .eq('customer_id', customerId);
    if (!data || data.length === 0) return null;
    return data.map((r: { book_id: string; qty: number }) => ({ bookId: r.book_id, qty: r.qty }));
  } catch {
    return null;
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const { customer } = useCustomerAuth();
  const router = useRouter();
  const prevCustomerRef = useRef<string | null>(null);
  // Debounce timer ref for DB sync
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist cart to localStorage AND DB whenever items change (only for logged-in customers)
  useEffect(() => {
    if (!customer) return;
    // Save to localStorage
    try {
      if (items.length === 0) {
        if (prevCustomerRef.current === customer.customerId) {
          localStorage.removeItem(`${CART_STORAGE_KEY}_${customer.customerId}`);
        }
      } else {
        const toSave = items.map(i => ({ bookId: i.book.id, qty: i.qty }));
        localStorage.setItem(`${CART_STORAGE_KEY}_${customer.customerId}`, JSON.stringify(toSave));
      }
    } catch { /* ignore */ }

    // Debounced DB sync — wait 600ms after last change before writing to DB
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      saveCartToDB(customer.customerId, items);
    }, 600);

    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [items, customer]);

  // Restore cart on login (prefer DB, fall back to localStorage), clear on logout
  useEffect(() => {
    const currentId = customer?.customerId ?? null;
    const prevId = prevCustomerRef.current;

    if (currentId && currentId !== prevId) {
      // Customer just logged in — restore their saved cart
      const restoreCart = async () => {
        try {
          const { createClient } = await import('@/lib/supabase/client');
          const supabase = createClient();

          // Try DB first
          let saved = await loadCartFromDB(currentId);

          // Fall back to localStorage if DB has nothing
          if (!saved) {
            const raw = localStorage.getItem(`${CART_STORAGE_KEY}_${currentId}`);
            if (raw) {
              saved = JSON.parse(raw) as { bookId: string; qty: number }[];
            }
          }

          if (!saved || saved.length === 0) return;

          const ids = saved.map(s => s.bookId);
          const { data: books } = await supabase
            .from('books')
            .select('*')
            .in('id', ids);

          if (!books) return;

          const restored: CartItem[] = [];
          for (const s of saved) {
            const book = books.find((b: Record<string, unknown>) => b.id === s.bookId);
            if (!book) continue;
            const mappedBook = mapBookFromRow(book as Record<string, unknown>);
            if (mappedBook.is_price_visible === false) continue;
            const isReservedSoldOut = Number(book.reserved) === 1;
            const isVisibilityReserved = book.visibility === 'Reserved';
            const available = (isReservedSoldOut || isVisibilityReserved)
              ? 0
              : Math.max(0, Number(book.inventory) - Number(book.reserved));
            const soldOut = available <= 0;
            const cappedQty = soldOut ? s.qty : Math.min(s.qty, available);
            restored.push({
              book: mappedBook,
              qty: cappedQty,
              soldOut,
            });
          }
          if (restored.length > 0) {
            setItems(restored);
          }
        } catch { /* ignore */ }
      };
      restoreCart();
    } else if (!currentId && prevId) {
      // Customer just logged out — clear in-memory cart and TikTok Live cart item
      setItems([]);
      try { localStorage.removeItem('ds_tl_cart_item'); } catch { /* ignore */ }
    }

    prevCustomerRef.current = currentId;
  }, [customer]);

  const addItem = useCallback((book: Book) => {
    // Never allow cart add when admin has hidden the price
    if (!isPriceVisible(book)) return;
    // Require login to add to cart
    if (!customer) {
      setShowSignupPrompt(true);
      return;
    }
    setItems(prev => {
      const existing = prev.find(i => i.book.id === book.id);
      // Compute available stock: reserved=1 means sold out regardless
      const isReservedSoldOut = (book.reserved ?? 0) === 1;
      const available = isReservedSoldOut ? 0 : Math.max(0, (book.inventory ?? 0) - (book.reserved ?? 0));
      if (existing) {
        // Do not exceed available stock
        if (existing.qty >= available) return prev; // silently cap
        return prev.map(i => i.book.id === book.id ? { ...i, qty: i.qty + 1 } : i);
      }
      if (available <= 0) return prev; // cannot add sold-out item
      return [...prev, { book, qty: 1 }];
    });
  }, [customer]);

  const removeItem = useCallback((bookId: string) => {
    setItems(prev => prev.filter(i => i.book.id !== bookId));
  }, []);

  const updateQty = useCallback((bookId: string, qty: number) => {
    if (qty <= 0) { removeItem(bookId); return; }
    setItems(prev => prev.map(i => {
      if (i.book.id !== bookId) return i;
      // Enforce stock cap
      const isReservedSoldOut = (i.book.reserved ?? 0) === 1;
      const available = isReservedSoldOut ? 0 : Math.max(0, (i.book.inventory ?? 0) - (i.book.reserved ?? 0));
      const cappedQty = Math.min(qty, available);
      if (cappedQty <= 0) return i; // don't allow 0 via updateQty (use removeItem)
      return { ...i, qty: cappedQty };
    }));
  }, [removeItem]);

  const clearCart = useCallback(() => {
    // Only clear in-memory cart; localStorage is cleared separately after checkout
    setItems([]);
  }, []);

  // Re-fetch book stock for all cart items and mark sold-out ones
  const refreshCartStock = useCallback(async () => {
    if (items.length === 0) return;
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const ids = items.map(i => i.book.id);
      const { data } = await supabase
        .from('books')
        .select('id, inventory, reserved, visibility, arrival_date, is_price_visible')
        .in('id', ids);
      if (!data) return;
      setItems(prev => prev
        .map(item => {
          const fresh = data.find((r: Record<string, unknown>) => r.id === item.book.id);
          if (!fresh) return item;
          const priceVisible = fresh.is_price_visible !== false;
          // Drop titles whose price was hidden after they were added
          if (!priceVisible) return null;
          const isReservedSoldOut = Number(fresh.reserved) === 1;
          const isVisibilityReserved = fresh.visibility === 'Reserved';
          const available = (isReservedSoldOut || isVisibilityReserved)
            ? 0
            : Math.max(0, Number(fresh.inventory) - Number(fresh.reserved));
          const soldOut = available <= 0;
          const cappedQty = soldOut ? item.qty : Math.min(item.qty, available);
          return {
            ...item,
            qty: cappedQty,
            soldOut,
            book: {
              ...item.book,
              inventory: Number(fresh.inventory),
              reserved: Number(fresh.reserved),
              available,
              is_price_visible: priceVisible,
            },
          };
        })
        .filter((item): item is CartItem => item !== null)
      );
    } catch {
      // ignore
    }
  }, [items]);

  const total = items
    .filter(i => !i.soldOut && isPriceVisible(i.book))
    .reduce((s, i) => s + i.book.final_srp * i.qty, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clearCart, total, refreshCartStock }}>
      {children}
      {/* Guest signup prompt */}
      {showSignupPrompt && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: 'rgba(10,6,2,0.97)' }}
        >
          <div
            className="relative w-full max-w-sm rounded-2xl overflow-hidden"
            style={{ background: '#1a0e06', border: '2px solid rgba(200,164,91,0.5)', boxShadow: '0 0 60px rgba(200,164,91,0.12)' }}
          >
            <div
              className="px-6 py-5 text-center"
              style={{ background: 'linear-gradient(135deg, rgba(200,164,91,0.15), rgba(139,69,19,0.12))', borderBottom: '1px solid rgba(200,164,91,0.3)' }}
            >
              <div className="text-3xl mb-2">🔒</div>
              <h2 className="font-display text-lg font-bold" style={{ color: '#F0DFC4' }}>Account Required</h2>
              <p className="text-xs mt-1" style={{ color: '#C8A45B' }}>You need an account to add items to your cart</p>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-center leading-relaxed" style={{ color: '#D4B896' }}>
                Create a free account to place preorders, track your orders, and get notified when your books arrive.
              </p>
              <button
                onClick={() => { setShowSignupPrompt(false); router.push('/signup'); }}
                className="btn-primary w-full py-3 text-sm"
              >
                Sign Up — It&apos;s Free ✦
              </button>
              <button
                onClick={() => { setShowSignupPrompt(false); router.push('/login'); }}
                className="w-full py-2.5 rounded-xl text-sm font-semibold"
                style={{ background: 'rgba(200,164,91,0.1)', color: '#C8A45B', border: '1px solid rgba(200,164,91,0.3)' }}
              >
                Log In
              </button>
              <button
                onClick={() => setShowSignupPrompt(false)}
                className="w-full text-xs py-1.5 text-center"
                style={{ color: '#A09080', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </CartContext.Provider>
  );
}

export function useCart() {
  return React.useContext(CartContext);
}

// ── Wishlist Account Prompt Modal ─────────────────────────
interface WishlistPromptProps {
  bookId: string;
  onClose: () => void;
  onSaved: () => void;
}

const WISHLIST_KEY = 'ds-wishlist';
const WISHLIST_ACCOUNT_KEY = 'ds-wishlist-account';

function WishlistAccountPrompt({ bookId, onClose, onSaved }: WishlistPromptProps) {
  const [tiktok, setTiktok] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const handle = tiktok.trim();
    if (!handle) { setError('Please enter your TikTok handle.'); return; }
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) { setError('PIN must be exactly 4 digits.'); return; }

    // Save account info
    localStorage.setItem(WISHLIST_ACCOUNT_KEY, JSON.stringify({ tiktok: handle, pin }));

    // Save book to wishlist
    try {
      const stored = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]') as string[];
      if (!stored.includes(bookId)) {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify([...stored, bookId]));
      }
    } catch { /* ignore */ }

    onSaved();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(44,26,14,0.97)' }}
    >
      <div
        className="relative w-full max-w-sm rounded-2xl animate-fade-in-up"
        style={{ background: '#3A2214', border: '1px solid rgba(200,164,91,0.4)', boxShadow: '0 8px 40px rgba(44,26,14,0.5)' }}
      >
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(200,164,91,0.25)' }}>
          <div className="flex items-center gap-2">
            <span style={{ color: '#E8C97A', fontSize: '18px' }}>♡</span>
            <span className="font-display text-sm font-bold" style={{ color: '#F0DFC4' }}>Save to Wishlist</span>
          </div>
          <button onClick={onClose} className="btn-ghost p-1 rounded-lg" style={{ color: '#C8A45B' }}>
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>
        <div className="p-6">
          <p className="text-sm mb-5" style={{ color: '#D4B896', lineHeight: '1.6' }}>
            Enter your TikTok handle and a 4-digit PIN to save this book to your wishlist.
          </p>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#C8A45B' }}>TikTok Handle</label>
              <input
                type="text"
                required
                value={tiktok}
                onChange={e => setTiktok(e.target.value)}
                className="input-field text-sm"
                placeholder="@yourtiktok"
                autoFocus
                style={{ background: '#2C1A0E', borderColor: 'rgba(200,164,91,0.4)', color: '#F0DFC4' }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#C8A45B' }}>4-Digit PIN</label>
              <input
                type="password"
                required
                maxLength={4}
                value={pin}
                onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                className="input-field text-sm text-center tracking-widest"
                placeholder="••••"
                style={{ background: '#2C1A0E', borderColor: 'rgba(200,164,91,0.4)', color: '#F0DFC4' }}
              />
            </div>
            {error && <p className="text-xs text-center" style={{ color: '#f87171' }}>{error}</p>}
            <button type="submit" className="btn-primary w-full py-2.5 text-sm">Save to Wishlist ♡</button>
            <button type="button" onClick={onClose} className="w-full text-xs text-center" style={{ color: '#A08070', background: 'none', border: 'none', cursor: 'pointer' }}>
              Cancel
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export { WishlistAccountPrompt, WISHLIST_KEY, WISHLIST_ACCOUNT_KEY };

// ── Admin Access Overlay ───────────────────────────────────
function AdminAccessOverlay({ onClose }: { onClose: () => void }) {
  const router = useRouter();
  const [accessCode, setAccessCode] = useState('');
  const [tiktok, setTiktok] = useState('');
  const [adminPin, setAdminPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function hashPin(pin: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(pin);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate access code
    if (accessCode.trim().toUpperCase() !== ADMIN_ACCESS_CODE) {
      setError('Invalid access code. Please try again.');
      return;
    }

    if (!tiktok.trim()) {
      setError('Please enter your TikTok handle.');
      return;
    }

    if (adminPin.length !== 6 || !/^\d{6}$/.test(adminPin)) {
      setError('Admin PIN must be exactly 6 digits.');
      return;
    }

    setLoading(true);
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const rawHandle = tiktok.trim();
      const handleNoAt = rawHandle.replace(/^@/, '');
      const handleWithAt = '@' + handleNoAt;

      let adminUser = null;

      const { data: d1 } = await supabase
        .from('admin_users')
        .select('id, tiktok_handle, pin_hash, pin_set, role, is_active, display_name')
        .eq('tiktok_handle', handleNoAt)
        .maybeSingle();

      if (d1) {
        adminUser = d1;
      } else {
        const { data: d2 } = await supabase
          .from('admin_users')
          .select('id, tiktok_handle, pin_hash, pin_set, role, is_active, display_name')
          .eq('tiktok_handle', handleWithAt)
          .maybeSingle();
        if (d2) adminUser = d2;
      }

      if (!adminUser) {
        setError('Administrator account not found. Please verify your TikTok handle.');
        setLoading(false);
        return;
      }

      if (!adminUser.is_active) {
        setError('This admin account has been deactivated.');
        setLoading(false);
        return;
      }

      const pinHashValue = (adminUser.pin_hash ?? '').trim();
      const pinIsSet = adminUser.pin_set === true;

      if (!pinIsSet || !pinHashValue) {
        // First-time admin — set their PIN
        const pinHash = await hashPin(adminPin);
        const { error: updateError } = await supabase
          .from('admin_users')
          .update({ pin_hash: pinHash, pin_set: true })
          .eq('id', adminUser.id);
        if (updateError) throw updateError;
        saveAdminSession(adminUser);
        onClose();
        router.push('/admin/inventory');
      } else {
        // Returning admin — verify PIN
        const pinHash = await hashPin(adminPin);
        if (pinHash !== adminUser.pin_hash) {
          setError('Incorrect PIN. Please try again.');
          setLoading(false);
          return;
        }
        saveAdminSession(adminUser);
        onClose();
        router.push('/admin/inventory');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(44,26,14,0.97)' }}
    >
      <div
        className="relative w-full max-w-sm rounded-2xl animate-fade-in-up"
        style={{ background: '#3A2214', border: '1px solid rgba(200,164,91,0.4)', boxShadow: '0 8px 40px rgba(44,26,14,0.5)' }}
      >
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="flex items-center gap-2">
            <AppLogo size={24} variant="full" />
            <span className="font-display text-sm font-bold" style={{ color: 'var(--primary-bright)' }}>Admin Portal</span>
          </div>
          <button onClick={onClose} className="btn-ghost p-1 rounded-lg">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-center mb-2">
              <h3 className="font-display text-base font-bold" style={{ color: '#F0DFC4' }}>Admin Sign In</h3>
              <p className="text-xs mt-1" style={{ color: 'rgba(200,164,91,0.7)' }}>Enter your credentials to access the admin portal</p>
            </div>

            {/* Access Code */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#C8A45B' }}>Access Code</label>
              <input
                type="password"
                required
                value={accessCode}
                onChange={e => setAccessCode(e.target.value)}
                className="input-field text-center tracking-widest"
                placeholder="••••••••"
                autoFocus
                suppressHydrationWarning
              />
            </div>

            {/* TikTok Handle */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#C8A45B' }}>TikTok Handle</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold" style={{ color: 'rgba(200,164,91,0.7)' }}>@</span>
                <input
                  type="text"
                  required
                  value={tiktok}
                  onChange={e => setTiktok(e.target.value)}
                  className="input-field pl-7 text-sm"
                  placeholder="your.tiktok.handle"
                  autoComplete="username"
                  suppressHydrationWarning
                />
              </div>
            </div>

            {/* PIN */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#C8A45B' }}>6-Digit Admin PIN</label>
              <input
                type="password"
                required
                maxLength={6}
                pattern="\d{6}"
                inputMode="numeric"
                value={adminPin}
                onChange={e => setAdminPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="input-field text-sm text-center tracking-widest"
                placeholder="••••••"
                autoComplete="current-password"
                suppressHydrationWarning
              />
              <p className="text-xs mt-1" style={{ color: 'rgba(160,128,112,0.8)' }}>
                First-time login? Your PIN will be set on first sign-in.
              </p>
            </div>

            {error && <p className="text-xs text-center" style={{ color: '#f87171' }}>{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-2.5 text-sm"
              style={{ opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Signing In...' : 'Sign In to Admin ✦'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Preorder Cart Drawer ───────────────────────────────────
function PreorderCartDrawer({ onClose, onCheckout, onTikTokLive, tiktokLiveEnabled }: { onClose: () => void; onCheckout: () => void; onTikTokLive: () => void; tiktokLiveEnabled?: boolean }) {
  const { items, removeItem, updateQty, total, refreshCartStock } = useCart();
  const { customer } = useCustomerAuth();
  const [waitlistHandle, setWaitlistHandle] = useState('');
  const [waitlistBookId, setWaitlistBookId] = useState<string | null>(null);
  const [waitlistBookTitle, setWaitlistBookTitle] = useState('');
  const [waitlistBookSku, setWaitlistBookSku] = useState('');
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState<string[]>([]);

  // TikTok Live cart item
  const [tlCartItem, setTlCartItem] = useState<{
    request_id: string;
    ref_number: string;
    book_title: string;
    price: number;
    tiktok_handle: string;
    customer_name: string;
  } | null>(null);

  // Load TikTok Live cart item from localStorage on mount
  useEffect(() => {
    if (!customer) {
      setTlCartItem(null);
      return;
    }
    try {
      const raw = localStorage.getItem('ds_tl_cart_item');
      if (raw) {
        setTlCartItem(JSON.parse(raw));
      }
    } catch { /* ignore */ }
  }, [customer]);

  const removeTlCartItem = () => {
    try { localStorage.removeItem('ds_tl_cart_item'); } catch { /* ignore */ }
    setTlCartItem(null);
  };

  // Refresh stock when drawer opens
  useEffect(() => {
    refreshCartStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSoldOutItems = items.some(i => i.soldOut);
  const hasAnyItems = items.length > 0 || tlCartItem !== null;

  const handleJoinWaitlist = async (item: CartItem) => {
    setWaitlistBookId(item.book.id);
    setWaitlistBookTitle(item.book.title);
    setWaitlistBookSku(item.book.sku);
  };

  const submitWaitlist = async () => {
    if (!waitlistHandle.trim() || !waitlistBookId) return;
    setWaitlistLoading(true);
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const handle = waitlistHandle.trim().replace(/^@/, '');
      await supabase.from('waitlist').insert({
        tiktok_handle: '@' + handle,
        book_id: waitlistBookId,
        book_sku: waitlistBookSku,
        book_title: waitlistBookTitle,
      });
      setWaitlistDone(prev => [...prev, waitlistBookId]);
      setWaitlistBookId(null);
      setWaitlistHandle('');
    } catch {
      // ignore
    } finally {
      setWaitlistLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.85)' }} onClick={onClose} />
      <div
        className="relative w-full max-w-sm h-full overflow-y-auto animate-fade-in flex flex-col"
        style={{ background: 'var(--background-card)', borderLeft: '1px solid var(--border)' }}
      >
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="flex items-center gap-2">
            <Icon name="ShoppingCartIcon" size={18} style={{ color: 'var(--primary-bright)' } as React.CSSProperties} />
            <span className="font-display text-base font-bold" style={{ color: 'var(--foreground)' }}>Cart</span>
            {(items.length > 0 || tlCartItem) && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'var(--primary)', color: '#fff' }}>
                {items.reduce((s, i) => s + i.qty, 0) + (tlCartItem ? 1 : 0)}
              </span>
            )}
          </div>
          <button onClick={onClose} className="btn-ghost p-1 rounded-lg">
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>

        {/* Sold-out warning banner */}
        {hasSoldOutItems && (
          <div className="mx-4 mt-3 rounded-xl p-3" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)' }}>
            <p className="text-xs font-semibold" style={{ color: '#f87171' }}>
              ⚠ Some items in your cart are now out of stock. Please remove them or join the waitlist before checking out.
            </p>
          </div>
        )}

        <div className="flex-1 p-4">
          {!hasAnyItems ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon name="ShoppingCartIcon" size={40} className="mb-4" style={{ color: 'var(--foreground-subtle)' } as React.CSSProperties} />
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--foreground-muted)' }}>Your cart is empty</p>
              <p className="text-xs" style={{ color: 'var(--foreground-subtle)' }}>Add books from the preorder list to get started.</p>
              <button onClick={onClose} className="btn-primary text-xs px-5 py-2 mt-4">Browse Books ✦</button>
              {tiktokLiveEnabled && (
                <div style={{ borderTop: '1px solid rgba(184,134,11,0.2)', paddingTop: '12px', marginTop: '16px', width: '100%' }}>
                  <p className="text-xs mb-2" style={{ color: 'var(--foreground-subtle)' }}>Purchased from our TikTok Live?</p>
                  <button
                    onClick={onTikTokLive}
                    className="w-full py-2.5 text-xs font-semibold rounded-xl flex items-center justify-center gap-2"
                    style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.35)' }}
                  >
                    <span>🎬</span>
                    TikTok Live Checkout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* TikTok Live cart item — display only, included in unified checkout */}
              {tlCartItem && (
                <div
                  className="rounded-xl p-3"
                  style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.3)' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-base">🎬</span>
                      <div>
                        <p className="text-xs font-bold" style={{ color: '#f87171' }}>TikTok Live Order</p>
                        <p className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>{tlCartItem.book_title}</p>
                        <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>Ref: {tlCartItem.ref_number}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-sm font-bold" style={{ color: 'var(--primary-bright)' }}>₱{tlCartItem.price.toLocaleString()}</span>
                      <button
                        onClick={removeTlCartItem}
                        className="text-xs"
                        style={{ color: '#f87171' }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <p className="text-xs mt-2" style={{ color: 'var(--foreground-subtle)' }}>
                    ✦ Included in checkout below
                  </p>
                </div>
              )}

              {/* Regular cart items */}
              {items.map(item => {
                const isReservedSoldOut = (item.book.reserved ?? 0) === 1;
                const available = isReservedSoldOut ? 0 : Math.max(0, (item.book.inventory ?? 0) - (item.book.reserved ?? 0));
                const alreadyWaitlisted = waitlistDone.includes(item.book.id);
                return (
                  <div
                    key={item.book.id}
                    className="flex items-start gap-3 rounded-xl p-3"
                    style={{
                      background: item.soldOut ? 'rgba(239,68,68,0.05)' : 'rgba(184,134,11,0.05)',
                      border: `1px solid ${item.soldOut ? 'rgba(239,68,68,0.3)' : 'var(--border)'}`,
                      opacity: item.soldOut ? 0.8 : 1,
                    }}
                  >
                    <div className="flex-shrink-0 w-10 h-14 rounded overflow-hidden relative" style={{ background: 'var(--muted)' }}>
                      <AppImage src={item.book.cover_url || '/assets/images/no_image.png'} alt={`Cover of ${item.book.title}`} width={40} height={56} className="w-full h-full object-cover" style={{ filter: item.soldOut ? 'grayscale(1)' : 'none' }} />
                      {item.soldOut && (
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
                          <span className="text-xs font-bold" style={{ color: '#f87171', fontSize: '8px', textAlign: 'center', lineHeight: 1.2 }}>OUT OF STOCK</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: item.soldOut ? '#f87171' : 'var(--foreground)' }}>{item.book.title}</p>
                      <p className="text-xs truncate" style={{ color: 'var(--foreground-muted)' }}>{item.book.author}</p>
                      {item.soldOut ? (
                        <div className="mt-1.5 space-y-1">
                          <p className="text-xs font-semibold" style={{ color: '#f87171' }}>Out of Stock</p>
                          <p className="text-xs" style={{ color: 'var(--foreground-subtle)' }}>Browse our other titles or join the waitlist.</p>
                          <div className="flex gap-1.5 mt-1">
                            {!alreadyWaitlisted ? (
                              <button
                                onClick={() => handleJoinWaitlist(item)}
                                className="text-xs px-2 py-1 rounded-lg font-semibold"
                                style={{ background: 'rgba(200,164,91,0.15)', color: '#C8A45B', border: '1px solid rgba(200,164,91,0.4)' }}
                              >
                                Join Waitlist
                              </button>
                            ) : (
                              <span className="text-xs px-2 py-1 rounded-lg font-semibold" style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' }}>
                                ✓ On Waitlist
                              </span>
                            )}
                            <button onClick={() => removeItem(item.book.id)} className="text-xs px-2 py-1 rounded-lg" style={{ color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' }}>Remove</button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs font-bold mt-0.5" style={{ color: 'var(--primary-bright)' }}>
                          {isPriceVisible(item.book)
                            ? `₱${(item.book.final_srp * item.qty).toLocaleString()}`
                            : 'Price TBA'}
                        </p>
                      )}
                    </div>
                    {!item.soldOut && (
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => updateQty(item.book.id, item.qty - 1)}
                            className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold"
                            style={{ background: 'var(--muted)', color: 'var(--foreground)' }}
                          >−</button>
                          <span className="text-xs font-bold w-5 text-center" style={{ color: 'var(--foreground)' }}>{item.qty}</span>
                          <button
                            onClick={() => updateQty(item.book.id, item.qty + 1)}
                            disabled={item.qty >= available}
                            className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold"
                            style={{
                              background: item.qty >= available ? 'rgba(120,100,80,0.1)' : 'var(--muted)',
                              color: item.qty >= available ? '#9E8E7E' : 'var(--foreground)',
                              cursor: item.qty >= available ? 'not-allowed' : 'pointer',
                            }}
                          >+</button>
                        </div>
                        {available > 0 && (
                          <span className="text-xs" style={{ color: 'var(--foreground-subtle)' }}>{available} left</span>
                        )}
                        <button onClick={() => removeItem(item.book.id)} className="text-xs" style={{ color: '#f87171' }}>Remove</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {hasAnyItems && (
          <div className="p-4" style={{ borderTop: '1px solid var(--border)' }}>
            {(items.length > 0 || tlCartItem) && (
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-semibold" style={{ color: 'var(--foreground-muted)' }}>Order Total</span>
                <span className="text-lg font-bold" style={{ color: 'var(--primary-bright)' }}>
                  ₱{(total + (tlCartItem ? tlCartItem.price : 0)).toLocaleString()}
                </span>
              </div>
            )}
            {hasSoldOutItems ? (
              <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}>
                <p className="text-xs font-semibold" style={{ color: '#f87171' }}>Remove out-of-stock items to proceed to checkout.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <button onClick={onCheckout} className="btn-primary w-full py-3 text-sm">
                  Proceed to Checkout ✦
                </button>
                {!tlCartItem && onTikTokLive && tiktokLiveEnabled && (
                  <button
                    onClick={onTikTokLive}
                    className="w-full py-2.5 text-sm font-semibold rounded-xl flex items-center justify-center gap-2"
                    style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.35)' }}
                  >
                    <span>🎬</span>
                    TikTok Live Checkout
                  </button>
                )}
              </div>
            )}
            <p className="text-xs text-center mt-2" style={{ color: 'var(--foreground-subtle)' }}>
              GCash payment · Shipping details collected after books arrive in the Philippines
            </p>
          </div>
        )}
      </div>

      {/* Waitlist modal */}
      {waitlistBookId && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(44,26,14,0.97)' }}>
          <div className="w-full max-w-sm rounded-2xl" style={{ background: '#3A2214', border: '1px solid rgba(200,164,91,0.4)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(200,164,91,0.25)' }}>
              <span className="font-display text-sm font-bold" style={{ color: '#F0DFC4' }}>Join Waitlist</span>
              <button onClick={() => setWaitlistBookId(null)} className="btn-ghost p-1 rounded-lg" style={{ color: '#C8A45B' }}>
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm" style={{ color: '#D4B896' }}>
                We&apos;ll notify you when <strong style={{ color: '#F0DFC4' }}>{waitlistBookTitle}</strong> is back in stock.
              </p>
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#C8A45B' }}>Your TikTok Handle</label>
                <input
                  type="text"
                  value={waitlistHandle}
                  onChange={e => setWaitlistHandle(e.target.value)}
                  className="input-field text-sm"
                  placeholder="@yourtiktok"
                  autoFocus
                  style={{ background: '#2C1A0E', borderColor: 'rgba(200,164,91,0.4)', color: '#F0DFC4' }}
                />
              </div>
              <button
                onClick={submitWaitlist}
                disabled={waitlistLoading || !waitlistHandle.trim()}
                className="btn-primary w-full py-2.5 text-sm"
                style={{ opacity: waitlistLoading || !waitlistHandle.trim() ? 0.7 : 1 }}
              >
                {waitlistLoading ? 'Joining...' : 'Join Waitlist ✦'}
              </button>
              <button onClick={() => setWaitlistBookId(null)} className="w-full text-xs text-center" style={{ color: '#A08070', background: 'none', border: 'none', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Nav Search with Admin Trigger ─────────────────────────
const ADMIN_TRIGGER = '1DS-ADMIN***';

const NavSearch = React.memo(function NavSearch({ onAdminTrigger }: { onAdminTrigger: () => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Book[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(async (q: string) => {
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const books = await getBooks({ search: q });
      // Filter out hidden titles — only show visible books
      const visible = books.filter(b => b.is_visible !== false);
      setResults(visible.slice(0, 8));
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    // Check for admin trigger keyword
    if (val === ADMIN_TRIGGER) {
      setQuery('');
      setOpen(false);
      setShowHint(false);
      onAdminTrigger();
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelect = (book: Book) => {
    setOpen(false);
    setShowHint(false);
    setQuery('');
    router.push(`/book-detail?id=${book.id}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); setShowHint(false); setQuery(''); }
  };

  const handleFocus = () => {
    if (!query.trim()) setShowHint(true);
  };

  const handleBlur = () => {
    // Delay to allow click events on dropdown to fire first
    setTimeout(() => setShowHint(false), 150);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowHint(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="flex-1 max-w-md hidden md:flex items-center relative">
      <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3.5 z-10 pointer-events-none" style={{ color: 'var(--foreground-subtle)' } as React.CSSProperties} />
      <input
        type="search"
        placeholder="Search books, authors, genres, Book Code..."
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="input-field pl-9 py-2 text-sm w-full"
        style={{ borderRadius: '9999px', paddingRight: '1rem' }}
        autoComplete="off"
      />
      {loading && (
        <div className="absolute right-3">
          <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--primary)' }} />
        </div>
      )}

      {/* Search hint dropdown — shown when focused with empty query */}
      {showHint && !query.trim() && !open && (
        <SearchHintDropdown hints={BOOK_SEARCH_HINTS} />
      )}

      {open && results.length > 0 && (
        <div
          className="absolute top-full left-0 right-0 mt-2 rounded-xl overflow-hidden z-50 shadow-2xl"
          style={{ background: 'var(--background-card)', border: '1px solid var(--border)', maxHeight: '420px', overflowY: 'auto' }}
        >
          {results.map(book => (
            <button
              key={book.id}
              onClick={() => handleSelect(book)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left transition-all hover:bg-opacity-80"
              style={{ borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(200,164,91,0.08)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div className="flex-shrink-0 w-10 h-14 rounded overflow-hidden" style={{ background: 'var(--muted)' }}>
                <AppImage src={book.cover_url || '/assets/images/no_image.png'} alt={`Cover of ${book.title}`} width={40} height={56} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>{book.title}</p>
                <p className="text-xs truncate" style={{ color: 'var(--foreground-muted)' }}>{book.author}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs" style={{ color: 'var(--foreground-subtle)' }}>{book.sku}</span>
                  <span className="text-xs" style={{ color: 'var(--foreground-subtle)' }}>· {book.format}</span>
                  {book.genre && <span className="text-xs" style={{ color: 'var(--foreground-subtle)' }}>· {book.genre}</span>}
                </div>
              </div>
              <div className="flex-shrink-0 text-sm font-bold" style={{ color: 'var(--primary-bright)' }}>
                {isPriceVisible(book) ? `₱${Number(book.final_srp).toLocaleString()}` : 'Price TBA'}
              </div>
            </button>
          ))}
          <div className="px-4 py-2 text-center">
            <button
              onClick={() => { setOpen(false); router.push(`/shop?search=${encodeURIComponent(query)}`); }}
              className="text-xs font-semibold"
              style={{ color: 'var(--primary-bright)' }}
            >
              View all results for &quot;{query}&quot; →
            </button>
          </div>
        </div>
      )}

      {open && query.trim() && results.length === 0 && !loading && (
        <div
          className="absolute top-full left-0 right-0 mt-2 rounded-xl z-50 shadow-2xl px-4 py-6 text-center"
          style={{ background: 'var(--background-card)', border: '1px solid var(--border)' }}
        >
          <p className="text-sm" style={{ color: 'var(--foreground-muted)' }}>No books found for &quot;{query}&quot;</p>
        </div>
      )}
    </div>
  );
});

const MobileNavSearch = React.memo(function MobileNavSearch({ onAdminTrigger }: { onAdminTrigger: () => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Book[]>([]);
  const [loading, setLoading] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const router = useRouter();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(async (q: string) => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    try {
      const books = await getBooks({ search: q });
      // Filter out hidden titles
      const visible = books.filter(b => b.is_visible !== false);
      setResults(visible.slice(0, 6));
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (val === ADMIN_TRIGGER) {
      setQuery('');
      setResults([]);
      onAdminTrigger();
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelect = (book: Book) => {
    setQuery('');
    setResults([]);
    router.push(`/book-detail?id=${book.id}`);
  };

  return (
    <div className="p-4" style={{ borderBottom: '1px solid var(--border)' }}>
      <div className="relative">
        <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--foreground-subtle)' } as React.CSSProperties} />
        <input
          type="search"
          placeholder="Search books..."
          value={query}
          onChange={handleChange}
          onFocus={() => { if (!query.trim()) setShowHint(true); }}
          onBlur={() => setTimeout(() => setShowHint(false), 150)}
          className="input-field pl-9 text-sm"
          autoComplete="off"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--primary)' }} />
          </div>
        )}
        {/* Search hint dropdown */}
        {showHint && !query.trim() && results.length === 0 && (
          <SearchHintDropdown hints={BOOK_SEARCH_HINTS} />
        )}
      </div>
      {results.length > 0 && (
        <div className="mt-2 rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
          {results.map(book => (
            <button
              key={book.id}
              onClick={() => handleSelect(book)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
              style={{ borderBottom: '1px solid var(--border)', background: 'var(--background-card)' }}
            >
              <div className="flex-shrink-0 w-8 h-11 rounded overflow-hidden" style={{ background: 'var(--muted)' }}>
                <AppImage src={book.cover_url || '/assets/images/no_image.png'} alt={`Cover of ${book.title}`} width={32} height={44} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate" style={{ color: 'var(--foreground)' }}>{book.title}</p>
                <p className="text-xs truncate" style={{ color: 'var(--foreground-muted)' }}>{book.author}</p>
              </div>
              <span className="text-xs font-bold flex-shrink-0" style={{ color: 'var(--primary-bright)' }}>
                {isPriceVisible(book) ? `₱${Number(book.final_srp).toLocaleString()}` : 'Price TBA'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

// ── Nav Links (no admin) ───────────────────────────────────
const BASE_NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'Shop', href: '/shop' },
  { label: 'On Hand', href: '/on-hand' },
  { label: 'Genres', href: '/genres' },
  { label: 'Collections', href: '/collections' },
  { label: 'Wishlist', href: '/wishlist' },
  { label: 'My Orders', href: '/orders' },
  { label: 'FAQs', href: '/faqs' },
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
];

// ── Notification Bell ──────────────────────────────────────
interface CustomerNotification {
  id: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  book_title?: string;
  order_ref?: string;
}

function NotificationBell() {
  const { customer } = useCustomerAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<CustomerNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const fetchNotifications = useCallback(async () => {
    if (!customer) return;
    setLoading(true);
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { data } = await supabase
        .from('customer_notifications')
        .select('id, title, message, is_read, created_at, book_title, order_ref')
        .eq('customer_id', customer.customerCode)
        .order('created_at', { ascending: false })
        .limit(20);
      setNotifications((data ?? []) as CustomerNotification[]);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [customer]);

  useEffect(() => {
    if (customer) fetchNotifications();
  }, [customer, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = async () => {
    setOpen(o => !o);
    if (!open && unreadCount > 0 && customer) {
      // Mark all as read
      try {
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();
        await supabase
          .from('customer_notifications')
          .update({ is_read: true })
          .eq('customer_id', customer.customerCode)
          .eq('is_read', false);
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      } catch {
        // ignore
      }
    }
  };

  const formatTime = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (!customer) return null;

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={handleOpen}
        className="btn-ghost p-2 rounded-lg relative"
        aria-label="Notifications"
        suppressHydrationWarning
      >
        <Icon name="BellIcon" size={18} style={{ color: 'var(--primary-bright)' } as React.CSSProperties} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
            style={{ background: '#ef4444', color: '#fff', fontSize: '10px' }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 rounded-xl overflow-hidden z-50 shadow-2xl"
          style={{ background: 'var(--background-card)', border: '1px solid var(--border)', top: '100%' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2">
              <Icon name="BellIcon" size={14} style={{ color: 'var(--primary-bright)' } as React.CSSProperties} />
              <span className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>Notifications</span>
            </div>
            {unreadCount === 0 && (
              <span className="text-xs" style={{ color: 'var(--foreground-subtle)' }}>All caught up ✓</span>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--primary)' }} />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-10 text-center px-4">
                <Icon name="BellIcon" size={28} className="mx-auto mb-3" style={{ color: 'var(--foreground-subtle)' } as React.CSSProperties} />
                <p className="text-sm font-semibold" style={{ color: 'var(--foreground-muted)' }}>No notifications yet</p>
                <p className="text-xs mt-1" style={{ color: 'var(--foreground-subtle)' }}>
                  You&apos;ll be notified when your ordered titles are on hand.
                </p>
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  className="px-4 py-3"
                  style={{
                    borderBottom: '1px solid var(--border)',
                    background: n.is_read ? 'transparent' : 'rgba(139,92,246,0.06)',
                  }}
                >
                  <div className="flex items-start gap-2">
                    {!n.is_read && (
                      <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#a78bfa' }} />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>{n.title}</p>
                      <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--foreground-muted)' }}>{n.message}</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--foreground-subtle)' }}>{formatTime(n.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2.5 text-center" style={{ borderTop: '1px solid var(--border)' }}>
              <Link
                href="/orders"
                onClick={() => setOpen(false)}
                className="text-xs font-semibold"
                style={{ color: 'var(--primary-bright)' }}
              >
                View My Orders →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Customer Account Dropdown ──────────────────────────────
function CustomerAccountDropdown({
  username,
  customerCode,
  myQueriesVisible,
  onLogout,
}: {
  username: string;
  customerCode: string;
  myQueriesVisible: boolean;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [idCopied, setIdCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { customer } = useCustomerAuth();

  // Admin PIN prompt state
  const [showAdminPinPrompt, setShowAdminPinPrompt] = useState(false);
  const [adminPinInput, setAdminPinInput] = useState('');
  const [adminPinError, setAdminPinError] = useState('');
  const [adminPinLoading, setAdminPinLoading] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);

  // Check if this customer is also an admin
  useEffect(() => {
    if (!customer) return;
    let cancelled = false;
    const checkAdmin = async () => {
      try {
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();
        const rawHandle = customer.tiktokHandle.replace(/^@/, '');
        const handleWithAt = '@' + rawHandle;
        const { data } = await supabase
          .from('admin_users')
          .select('id, is_active')
          .or(`tiktok_handle.eq.${rawHandle},tiktok_handle.eq.${handleWithAt}`)
          .eq('is_active', true)
          .maybeSingle();
        if (!cancelled) setIsAdminUser(!!data);
      } catch { /* ignore */ }
    };
    checkAdmin();
    return () => { cancelled = true; };
  }, [customer]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleCopyId = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(customerCode).then(() => {
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 2000);
    });
  };

  const handleAdminAccess = () => {
    setOpen(false);
    setAdminPinInput('');
    setAdminPinError('');
    setShowAdminPinPrompt(true);
  };

  const handleAdminPinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (adminPinInput.length !== 6 || !/^\d{6}$/.test(adminPinInput)) {
      setAdminPinError('PIN must be exactly 6 digits.');
      return;
    }
    setAdminPinLoading(true);
    setAdminPinError('');
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const rawHandle = customer!.tiktokHandle.replace(/^@/, '');
      const handleWithAt = '@' + rawHandle;

      const { data: d1 } = await supabase
        .from('admin_users')
        .select('id, tiktok_handle, pin_hash, pin_set, role, is_active, display_name')
        .eq('tiktok_handle', rawHandle)
        .maybeSingle();
      const { data: d2 } = !d1 ? await supabase
        .from('admin_users')
        .select('id, tiktok_handle, pin_hash, pin_set, role, is_active, display_name')
        .eq('tiktok_handle', handleWithAt)
        .maybeSingle() : { data: null };

      let adminUser = d1 || d2;
      if (!adminUser || !adminUser.is_active) {
        setAdminPinError('Admin account not found or inactive.');
        setAdminPinLoading(false);
        return;
      }

      // Hash the entered PIN (no salt — same as admin login)
      const encoder = new TextEncoder();
      const data = encoder.encode(adminPinInput);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const enteredHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      const pinHashValue = (adminUser.pin_hash ?? '').trim();
      const pinIsSet = adminUser.pin_set === true;

      if (!pinIsSet || !pinHashValue) {
        // First-time: set PIN
        const { error: updateError } = await supabase
          .from('admin_users')
          .update({ pin_hash: enteredHash, pin_set: true })
          .eq('id', adminUser.id);
        if (updateError) throw updateError;
      } else {
        if (enteredHash !== pinHashValue) {
          setAdminPinError('Incorrect PIN. Please try again.');
          setAdminPinLoading(false);
          return;
        }
      }

      // Save admin session and redirect
      const { saveAdminSession } = await import('@/lib/admin-auth');
      saveAdminSession(adminUser);
      setShowAdminPinPrompt(false);
      router.push('/admin/inventory');
    } catch (err: unknown) {
      setAdminPinError(err instanceof Error ? err.message : 'Authentication failed. Please try again.');
    } finally {
      setAdminPinLoading(false);
    }
  };

  return (
    <>
      {/* Admin PIN Prompt Modal */}
      {showAdminPinPrompt && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: 'rgba(10,6,2,0.97)' }}
        >
          <div
            className="relative w-full max-w-sm rounded-2xl animate-fade-in-up"
            style={{ background: '#1a0e06', border: '2px solid rgba(200,164,91,0.5)', boxShadow: '0 0 60px rgba(200,164,91,0.12)' }}
          >
            <div
              className="px-6 py-5 text-center"
              style={{ background: 'linear-gradient(135deg, rgba(200,164,91,0.15), rgba(139,69,19,0.12))', borderBottom: '1px solid rgba(200,164,91,0.3)' }}
            >
              <div className="text-3xl mb-2">🔐</div>
              <h2 className="font-display text-lg font-bold" style={{ color: '#F0DFC4' }}>Admin Access</h2>
              <p className="text-xs mt-1" style={{ color: '#C8A45B' }}>Enter your 6-digit admin PIN to continue</p>
            </div>
            <div className="p-6">
              <form onSubmit={handleAdminPinSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: '#C8A45B' }}>6-Digit Admin PIN</label>
                  <input
                    type="password"
                    required
                    maxLength={6}
                    inputMode="numeric"
                    value={adminPinInput}
                    onChange={e => setAdminPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input-field text-sm text-center tracking-widest w-full"
                    placeholder="••••••"
                    autoFocus
                    style={{ background: '#2C1A0E', borderColor: 'rgba(200,164,91,0.4)', color: '#F0DFC4' }}
                  />
                  {adminPinError && (
                    <p className="text-xs text-center mt-2" style={{ color: '#f87171' }}>{adminPinError}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={adminPinLoading || adminPinInput.length !== 6}
                  className="btn-primary w-full py-3 text-sm"
                  style={{ opacity: adminPinLoading || adminPinInput.length !== 6 ? 0.7 : 1 }}
                >
                  {adminPinLoading ? 'Verifying...' : 'Enter Admin Dashboard ✦'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAdminPinPrompt(false)}
                  className="w-full text-xs py-1.5 text-center"
                  style={{ color: '#A09080', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <div ref={ref} className="relative">
        <button
          onClick={() => setOpen(o => !o)}
          className="btn-ghost px-3 py-1.5 rounded-lg text-xs font-semibold hidden md:flex items-center gap-1.5"
          style={{ color: 'var(--primary-bright)' }}
        >
          <Icon name="UserCircleIcon" size={14} style={{ color: 'var(--primary-bright)' } as React.CSSProperties} />
          {username}
          <Icon name="ChevronDownIcon" size={12} style={{ color: 'var(--primary-bright)', opacity: 0.7 } as React.CSSProperties} />
        </button>

        {open && (
          <div
            className="absolute right-0 mt-1 w-52 rounded-xl overflow-hidden z-50 shadow-2xl"
            style={{ background: 'var(--background-card)', border: '1px solid var(--border)', top: '100%' }}
          >
            {/* Customer ID row */}
            <div
              className="px-4 py-2.5"
              style={{ borderBottom: '1px solid var(--border)', background: 'rgba(139,92,246,0.06)' }}
            >
              <p className="text-xs font-bold mb-1" style={{ color: '#a78bfa' }}>Customer ID</p>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono flex-1 truncate" style={{ color: '#4B352A', letterSpacing: '0.05em' }}>{customerCode}</span>
                <button
                  onClick={handleCopyId}
                  className="flex-shrink-0 text-xs px-2 py-0.5 rounded font-semibold"
                  style={{ background: 'rgba(200,164,91,0.18)', color: '#7B5C2A', border: '1px solid rgba(200,164,91,0.55)' }}
                >
                  {idCopied ? '✓' : 'Copy'}
                </button>
              </div>
            </div>
            <Link
              href="/orders"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold transition-colors"
              style={{ color: 'var(--foreground-muted)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(200,164,91,0.08)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <Icon name="ShoppingBagIcon" size={13} style={{ color: 'var(--primary-bright)' } as React.CSSProperties} />
              My Orders
            </Link>
            <Link
              href="/tiktok-live-checkout"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold transition-colors"
              style={{ color: 'var(--foreground-muted)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(200,164,91,0.08)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ fontSize: '13px' }}>🎬</span>
              TikTok Live Checkout
            </Link>
            {myQueriesVisible && (
              <Link
                href="/my-queries"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold transition-colors"
                style={{ color: 'var(--foreground-muted)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(200,164,91,0.08)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <Icon name="ChatBubbleLeftRightIcon" size={13} style={{ color: 'var(--primary-bright)' } as React.CSSProperties} />
                My Queries
              </Link>
            )}
            {/* Admin access — only shown if customer is also an admin */}
            {isAdminUser && (
              <>
                <div style={{ height: '1px', background: 'var(--border)', margin: '2px 0' }} />
                <button
                  onClick={handleAdminAccess}
                  className="flex items-center gap-2 px-4 py-2.5 text-xs w-full text-left transition-colors font-semibold"
                  style={{ color: '#C8A45B' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(200,164,91,0.08)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <Icon name="Cog6ToothIcon" size={13} style={{ color: '#C8A45B' } as React.CSSProperties} />
                  Admin Dashboard ✦
                </button>
              </>
            )}
            <div style={{ height: '1px', background: 'var(--border)', margin: '2px 0' }} />
            <button
              onClick={() => { onLogout(); setOpen(false); }}
              className="flex items-center gap-2 px-4 py-2.5 text-xs w-full text-left transition-colors"
              style={{ color: 'var(--foreground-subtle)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.06)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <Icon name="ArrowRightOnRectangleIcon" size={13} />
              Log Out
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default function Navbar() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [adminOverlay, setAdminOverlay] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const { items } = useCart();
  const cartCount = items.reduce((s, i) => s + i.qty, 0);
  const [announcements, setAnnouncements] = useState<{ id: string; title: string; message: string; type: string }[]>([]);
  const [dismissedBanners, setDismissedBanners] = useState<string[]>([]);
  const { customer, logout: customerLogout } = useCustomerAuth();
  const [myInquiriesVisible, setMyInquiriesVisible] = useState(false);
  const [onlinePaymentEnabled, setOnlinePaymentEnabled] = useState(false);
  const [tiktokLiveCheckoutEnabled, setTiktokLiveCheckoutEnabled] = useState(false);
  const [tlCartItemBadge, setTlCartItemBadge] = useState(false);
  const [tlCartItemForCheckout, setTlCartItemForCheckout] = useState<{
    request_id: string;
    ref_number: string;
    book_title: string;
    price: number;
    tiktok_handle: string;
    customer_name: string;
  } | null>(null);

  // Build nav links — conditionally include My Queries
  const NAV_LINKS = myInquiriesVisible
    ? [...BASE_NAV_LINKS.slice(0, 9), { label: 'My Queries', href: '/my-queries' }, BASE_NAV_LINKS[9]]
    : BASE_NAV_LINKS;

  // Load TikTok Live cart item presence for badge
  useEffect(() => {
    const checkTlCart = () => {
      try {
        const raw = localStorage.getItem('ds_tl_cart_item');
        setTlCartItemBadge(!!raw);
      } catch { /* ignore */ }
    };
    checkTlCart();
    // Listen for storage changes (e.g. when item is added/removed from another tab or component)
    window.addEventListener('storage', checkTlCart);
    return () => window.removeEventListener('storage', checkTlCart);
  }, []);

  // Re-check TikTok Live cart badge whenever cart opens (same-tab add scenario)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('ds_tl_cart_item');
      setTlCartItemBadge(!!raw);
    } catch { /* ignore */ }
  }, [cartOpen]);

  // Load My Inquiries visibility setting and Online Payment setting
  useEffect(() => {
    let cancelled = false;
    const fetchSetting = async () => {
      try {
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();
        const { data } = await supabase
          .from('homepage_settings')
          .select('value')
          .eq('key', 'my_inquiries_visible')
          .maybeSingle();
        if (!cancelled && data) {
          setMyInquiriesVisible(data.value === 'true');
        }
        const { data: paymentData } = await supabase
          .from('homepage_settings')
          .select('value')
          .eq('key', 'online_payment_enabled')
          .maybeSingle();
        if (!cancelled && paymentData) {
          setOnlinePaymentEnabled(paymentData.value === 'true');
        }
        const { data: tiktokData } = await supabase
          .from('homepage_settings')
          .select('value')
          .eq('key', 'tiktok_live_checkout_enabled')
          .maybeSingle();
        if (!cancelled && tiktokData) {
          setTiktokLiveCheckoutEnabled(tiktokData.value === 'true');
        }
      } catch { /* ignore */ }
    };
    fetchSetting();
    return () => { cancelled = true; };
  }, []);

  // Load active announcements (client-side date filter — ISO timestamps break PostgREST .or() filters)
  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let channel: any = null;

    const fetchAnnouncements = async () => {
      try {
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();
        const { data, error } = await supabase
          .from('announcements')
          .select('id, title, message, type, starts_at, ends_at')
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(20);
        if (error || cancelled) return;
        const now = Date.now();
        const active = (data ?? [])
          .filter(a => {
            if (a.starts_at && new Date(a.starts_at).getTime() > now) return false;
            if (a.ends_at && new Date(a.ends_at).getTime() < now) return false;
            return true;
          })
          .slice(0, 3)
          .map(({ id, title, message, type }) => ({ id, title, message, type }));
        setAnnouncements(active);
      } catch { /* ignore */ }
    };

    const setup = async () => {
      await fetchAnnouncements();
      try {
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();
        channel = supabase
          .channel('announcements_sync')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'announcements' }, () => {
            fetchAnnouncements();
          })
          .subscribe();
      } catch { /* ignore */ }
    };
    setup();

    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchAnnouncements();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (channel) {
        import('@/lib/supabase/client').then(({ createClient }) => {
          createClient().removeChannel(channel);
        });
      }
    };
  }, []);

  const visibleBanners = announcements.filter(a => !dismissedBanners.includes(a.id));

  const BANNER_COLORS: Record<string, { bg: string; border: string; color: string; title: string }> = {
    info: { bg: '#1a3352', border: '#60a5fa', color: '#dbeafe', title: '#ffffff' },
    warning: { bg: '#713f12', border: '#fbbf24', color: '#fef9c3', title: '#ffffff' },
    success: { bg: '#14532d', border: '#34d399', color: '#d1fae5', title: '#ffffff' },
    promo: { bg: '#3d2e1f', border: '#C8A45B', color: '#f5ebe0', title: '#FFF8F0' },
    urgent: { bg: '#7f1d1d', border: '#f87171', color: '#fecaca', title: '#ffffff' },
  };

  const handleAdminTrigger = useCallback(() => {
    setMobileOpen(false);
    setAdminOverlay(true);
  }, []);

  const handleCheckout = useCallback(() => {
    // Read TikTok Live cart item from localStorage at checkout time
    let tlItem = null;
    try {
      const raw = localStorage.getItem('ds_tl_cart_item');
      if (raw) tlItem = JSON.parse(raw);
    } catch { /* ignore */ }
    setTlCartItemForCheckout(tlItem);
    setCartOpen(false);
    setCheckoutOpen(true);
  }, []);

  const [tikTokModalOpen, setTikTokModalOpen] = useState(false);
  const [tikTokSubmittedRef, setTikTokSubmittedRef] = useState<string | null>(null);

  const handleTikTokLive = useCallback(() => {
    setCartOpen(false);
    setTikTokModalOpen(true);
  }, []);

  // Open cart drawer when ?openCart=true is in the URL
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('openCart') === 'true') {
      setCartOpen(true);
      // Remove the param from the URL without a page reload
      const url = new URL(window.location.href);
      url.searchParams.delete('openCart');
      window.history.replaceState({}, '', url.toString());
    }
  }, [pathname]);

  return (
    <>
      {/* Announcement Banners — stacked with per-banner offset */}
      {visibleBanners.map((banner, idx) => {
        const style = BANNER_COLORS[banner.type] ?? BANNER_COLORS.info;
        return (
          <div
            key={banner.id}
            className="fixed left-0 right-0 z-50 flex items-center justify-between px-4 py-2.5 text-sm font-medium"
            style={{
              top: `${idx * 40}px`,
              background: style.bg,
              borderBottom: `1px solid ${style.border}`,
              color: style.color,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            <div className="flex-1 text-center leading-snug">
              <strong style={{ color: style.title }}>{banner.title}:</strong>{' '}
              {banner.message}
            </div>
            <button
              onClick={() => setDismissedBanners(prev => [...prev, banner.id])}
              className="ml-3 flex-shrink-0 opacity-80 hover:opacity-100"
              style={{ color: style.title }}
              aria-label="Dismiss announcement"
            >
              ✕
            </button>
          </div>
        );
      })}
      <nav
        className="fixed left-0 right-0 z-40"
        style={{
          top: visibleBanners.length > 0 ? `${visibleBanners.length * 40}px` : '0px',
          background: 'linear-gradient(180deg, rgba(20,12,4,0.98) 0%, rgba(20,12,4,0.94) 100%)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid var(--border)',
          boxShadow: '0 2px 20px rgba(0,0,0,0.4)',
          transition: 'top 0.2s',
        }}
      >
        <div className="content-wrapper">
          <div className="flex items-center justify-between h-16 gap-4">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2 flex-shrink-0">
              <AppLogo size={44} variant="full" />
              <span className="font-display text-base font-bold hidden sm:block" style={{ color: 'var(--primary-bright)' }}>Daddee&apos;s Shelf</span>
            </Link>

            {/* Live Search Bar */}
            <NavSearch onAdminTrigger={handleAdminTrigger} />

            {/* Desktop Nav */}
            <div className="hidden xl:flex items-center gap-1 flex-shrink-0">
              {NAV_LINKS.slice(0, 7).map(link => (
                <Link
                  key={`nav-${link.href}`}
                  href={link.href}
                  className={`nav-link px-3 py-1.5 rounded-lg text-xs ${pathname === link.href ? 'active' : ''}`}
                  style={pathname === link.href ? { background: 'var(--primary-glow)' } : {}}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Preorder Cart + Mobile Toggle */}
            <div className="flex items-center gap-2">
              {/* Notification Bell — logged-in customers only */}
              <NotificationBell />

              {/* Customer account button */}
              {customer ? (
                <div className="hidden sm:flex items-center gap-1">
                  <CustomerAccountDropdown
                    username={customer.username}
                    customerCode={customer.customerCode}
                    myQueriesVisible={myInquiriesVisible}
                    onLogout={customerLogout}
                  />
                </div>
              ) : (
                <Link
                  href="/login"
                  className="btn-ghost px-3 py-1.5 rounded-lg text-xs font-semibold hidden md:flex items-center gap-1.5"
                  style={{ color: 'var(--foreground-muted)' }}
                >
                  <Icon name="UserCircleIcon" size={14} style={{ color: 'var(--foreground-subtle)' } as React.CSSProperties} />
                  Log In
                </Link>
              )}

              <button
                onClick={() => setCartOpen(true)}
                className="btn-ghost p-2 rounded-lg hidden sm:flex relative"
                aria-label="Cart"
                suppressHydrationWarning
              >
                <Icon name="ShoppingCartIcon" size={18} style={{ color: 'var(--primary-bright)' } as React.CSSProperties} />
                {(cartCount > 0 || tlCartItemBadge) && (
                  <span
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: 'var(--primary)', color: '#fff', fontSize: '10px' }}
                  >
                    {cartCount + (tlCartItemBadge ? 1 : 0)}
                  </span>
                )}
              </button>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="btn-ghost p-2 rounded-lg xl:hidden"
                aria-label="Open menu"
                suppressHydrationWarning
              >
                <Icon name="Bars3Icon" size={20} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
            onClick={() => setMobileOpen(false)}
          />
          <div
            className="relative ml-auto w-72 h-full overflow-y-auto animate-fade-in"
            style={{ background: 'rgba(44,26,14,0.97)', borderLeft: '1px solid rgba(200,164,91,0.3)', backdropFilter: 'blur(16px)' }}
          >
            <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(200,164,91,0.25)' }}>
              <span className="font-display text-base font-semibold" style={{ color: '#F0DFC4' }}>Navigation</span>
              <button onClick={() => setMobileOpen(false)} className="btn-ghost p-1" style={{ color: '#C8A45B' }}>
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>

            <MobileNavSearch onAdminTrigger={handleAdminTrigger} />

            {/* Cart button in mobile */}
            <div className="px-3 pt-3">
              <button
                onClick={() => { setMobileOpen(false); setCartOpen(true); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm btn-primary"
              >
                <Icon name="ShoppingCartIcon" size={16} />
                <span>Cart</span>
                {(cartCount > 0 || tlCartItemBadge) && (
                  <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }}>
                    {cartCount + (tlCartItemBadge ? 1 : 0)}
                  </span>
                )}
              </button>
            </div>

            <div className="p-3 flex flex-col gap-1">
              {NAV_LINKS.map(link => (
                <Link
                  key={`mobile-nav-${link.href}`}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all`}
                  style={pathname === link.href
                    ? { background: 'rgba(200,164,91,0.25)', color: '#F0DFC4', fontWeight: 600 }
                    : { color: '#D4B896' }
                  }
                >
                  {link.label}
                </Link>
              ))}

              {/* Customer auth links in mobile */}
              <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(200,164,91,0.2)' }}>
                {customer ? (
                  <>
                    <div className="px-3 py-2 text-xs" style={{ color: '#C8A45B' }}>
                      Logged in as <strong style={{ color: '#F0DFC4' }}>{customer.username}</strong>
                    </div>
                    <button
                      onClick={() => { customerLogout(); setMobileOpen(false); }}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm w-full text-left"
                      style={{ color: '#D4B896' }}
                    >
                      Log Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      href="/login"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm"
                      style={{ color: '#D4B896' }}
                    >
                      Log In
                    </Link>
                    <Link
                      href="/signup"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm"
                      style={{ color: '#C8A45B', fontWeight: 600 }}
                    >
                      Sign Up
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preorder Cart Drawer */}
      {cartOpen && (
        <PreorderCartDrawer
          onClose={() => setCartOpen(false)}
          onCheckout={handleCheckout}
          onTikTokLive={handleTikTokLive}
          tiktokLiveEnabled={tiktokLiveCheckoutEnabled}
        />
      )}

      {/* TikTok Live Checkout Modal */}
      {tikTokModalOpen && (
        <TikTokLiveCheckoutModal
          onClose={() => setTikTokModalOpen(false)}
          onSubmitted={(_requestId, refNumber) => {
            setTikTokModalOpen(false);
            setTikTokSubmittedRef(refNumber);
          }}
        />
      )}

      {/* TikTok Live submission confirmation */}
      {tikTokSubmittedRef && (
        <div
          className="fixed bottom-6 right-6 z-[200] rounded-xl p-4 max-w-xs"
          style={{ background: '#1a0e06', border: '1px solid rgba(16,185,129,0.4)', boxShadow: '0 4px 24px rgba(0,0,0,0.5)' }}
        >
          <p className="text-xs font-bold mb-1" style={{ color: '#10b981' }}>✓ TikTok Live Request Submitted!</p>
          <p className="text-xs mb-1" style={{ color: '#D4B896' }}>
            Reference: <strong style={{ color: '#F0DFC4' }}>{tikTokSubmittedRef}</strong>
          </p>
          <p className="text-xs mb-2" style={{ color: '#8a7060' }}>We&apos;ll verify your screenshot and notify you when ready.</p>
          <div className="flex gap-2">
            <Link href="/tiktok-live-checkout" className="text-xs font-semibold" style={{ color: '#C8A45B' }}>
              Track request →
            </Link>
            <button onClick={() => setTikTokSubmittedRef(null)} className="text-xs ml-auto" style={{ color: '#8a7060' }}>✕</button>
          </div>
        </div>
      )}

      {/* Admin Access Overlay */}
      {adminOverlay && (
        <AdminAccessOverlay onClose={() => setAdminOverlay(false)} />
      )}

      {/* Checkout Modal — rendered inline via preorder-list page */}
      {checkoutOpen && (
        <CheckoutRedirectModal
          onClose={() => setCheckoutOpen(false)}
          onlinePaymentEnabled={onlinePaymentEnabled}
          tlCartItem={tlCartItemForCheckout}
        />
      )}
    </>
  );
}

// ── Checkout Redirect Modal ────────────────────────────────
function CheckoutRedirectModal({
  onClose,
  onlinePaymentEnabled,
  tlCartItem,
}: {
  onClose: () => void;
  onlinePaymentEnabled: boolean;
  tlCartItem: {
    request_id: string;
    ref_number: string;
    book_title: string;
    price: number;
    tiktok_handle: string;
    customer_name: string;
  } | null;
}) {
  const router = useRouter();
  const { items, clearCart } = useCart();
  const { customer } = useCustomerAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'gcash' | 'paymongo'>('gcash');
  const [paymongoLoading, setPaymongoLoading] = useState(false);
  const [paymongoError, setPaymongoError] = useState('');
  const [convenienceFee, setConvenienceFee] = useState<number | null>(null);
  const [form, setForm] = useState({
    tiktok_handle: '',
    customer_pin: '',
    payment_ref: '',
    notes: '',
    user_slug: '',
  });
  const [pileShipping, setPileShipping] = useState(false);
  const [onlineNotes, setOnlineNotes] = useState('');
  const [onlinePileShipping, setOnlinePileShipping] = useState(false);
  const [confirmation, setConfirmation] = useState<{
    order_ref: string;
    tiktok_handle: string;
    user_slug: string;
    is_new_slug: boolean;
    order_items: { title: string; qty: number; price: number }[];
    order_total: number;
    order_date: string;
    tl_included: boolean;
    is_pile_shipping: boolean;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [slugCopied, setSlugCopied] = useState(false);
  const [qrExpanded, setQrExpanded] = useState(false);

  // ── Existing slug check ───────────────────────────────────
  const [existingSlug, setExistingSlug] = useState<string | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const slugCheckRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Store Credit ──────────────────────────────────────────
  const [storeCredit, setStoreCredit] = useState<{ id: string; amount: number } | null>(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const creditLookupRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const rawTotal = items
    .filter(i => !i.soldOut && isPriceVisible(i.book))
    .reduce((s, i) => s + i.book.final_srp * i.qty, 0);
  const creditApplied = storeCredit ? Math.min(storeCredit.amount, rawTotal) : 0;
  // Include TikTok Live item price in total (store credit only applies to regular items)
  const total = Math.max(0, rawTotal - creditApplied) + (tlCartItem ? tlCartItem.price : 0);

  // For logged-in customers, use their handle for slug/credit checks
  const effectiveHandle = customer ? customer.tiktokHandle : form.tiktok_handle;

  // Check for existing slug when handle changes (guest only)
  useEffect(() => {
    if (customer) {
      // Logged-in: set existingSlug from their customerCode
      setExistingSlug(customer.customerCode);
      return;
    }
    if (slugCheckRef.current) clearTimeout(slugCheckRef.current);
    const handle = form.tiktok_handle.trim();
    if (!handle) { setExistingSlug(null); return; }
    slugCheckRef.current = setTimeout(async () => {
      setSlugChecking(true);
      try {
        const { supabase: sb } = await import('@/lib/supabase');
        const rawHandle = handle.replace(/^@/, '');
        const handleWithAt = '@' + rawHandle;

        // First check customer_slugs table (legacy/anonymous orders)
        const { data: slugData } = await sb
          .from('customer_slugs')
          .select('user_slug')
          .or(`tiktok_handle.eq.${rawHandle},tiktok_handle.eq.${handleWithAt}`)
          .maybeSingle();

        if (slugData?.user_slug) {
          setExistingSlug(slugData.user_slug);
        } else {
          // Also check customers table for registered accounts (customer_id field)
          const { data: customerData } = await sb
            .from('customers')
            .select('customer_id')
            .or(`tiktok_handle.eq.${rawHandle},tiktok_handle.eq.${handleWithAt}`)
            .not('customer_id', 'is', null)
            .maybeSingle();
          setExistingSlug(customerData?.customer_id ?? null);
        }
      } catch { setExistingSlug(null); }
      setSlugChecking(false);
    }, 600);
    return () => { if (slugCheckRef.current) clearTimeout(slugCheckRef.current); };
  }, [form.tiktok_handle, customer]);

  // Lookup store credit whenever effective handle changes (debounced)
  useEffect(() => {
    if (creditLookupRef.current) clearTimeout(creditLookupRef.current);
    const handle = effectiveHandle.trim();
    if (!handle) {
      setStoreCredit(null);
      return;
    }
    creditLookupRef.current = setTimeout(async () => {
      setCreditLoading(true);
      try {
        const { supabase: sb } = await import('@/lib/supabase');
        const rawHandle = handle.replace(/^@/, '');
        const handleWithAt = '@' + rawHandle;
        const { data } = await sb
          .from('store_credits')
          .select('id, amount, tiktok_handle')
          .eq('is_active', true)
          .eq('status', 'Active')
          .is('used_on_order_ref', null)
          .or(`tiktok_handle.eq.${rawHandle},tiktok_handle.eq.${handleWithAt}`)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        setStoreCredit(data ? { id: data.id, amount: Number(data.amount) } : null);
      } catch {
        setStoreCredit(null);
      } finally {
        setCreditLoading(false);
      }
    }, 600);
    return () => {
      if (creditLookupRef.current) clearTimeout(creditLookupRef.current);
    };
  }, [effectiveHandle]);

  async function hashPin(pin: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(pin + 'daddees-shelf-salt');
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  function generateOrderRef(): string {
    const now = new Date();
    const date = now.toISOString().slice(0, 10).replace(/-/g, '');
    const seq = String(Math.floor(Math.random() * 9000) + 1000).padStart(4, '0');
    return `DDS-${date}-${seq}`;
  }

  function generateUserSlug(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let slug = 'DS-';
    for (let i = 0; i < 8; i++) {
      slug += chars[Math.floor(Math.random() * chars.length)];
    }
    return slug;
  }

  const handlePayMongo = async () => {
    setPaymongoError('');
    const purchasableItems = items.filter(i => !i.soldOut && isPriceVisible(i.book));
    if (purchasableItems.length === 0 && !tlCartItem) {
      setPaymongoError('No purchasable items in cart.');
      return;
    }

    // Validate required fields before redirecting
    if (!customer && !form.tiktok_handle.trim()) {
      setPaymongoError('Please enter your TikTok handle before proceeding.');
      setPaymentMethod('gcash'); // switch to show the form
      return;
    }

    setPaymongoLoading(true);
    try {
      const payload = purchasableItems.map(i => ({
        name: i.book.title,
        amount: Math.round(i.book.final_srp * 100), // convert pesos → centavos
        quantity: i.qty,
      }));

      // Include TikTok Live item in PayMongo line items if present
      if (tlCartItem) {
        payload.push({
          name: `🎬 ${tlCartItem.book_title} (TikTok Live)`,
          amount: Math.round(tlCartItem.price * 100),
          quantity: 1,
        });
      }

      // Build order data to pass as metadata (order is created AFTER payment is confirmed)
      const orderRef = generateOrderRef();
      const orderDate = new Date().toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' });
      const resolvedHandle = customer ? customer.tiktokHandle : form.tiktok_handle.trim();
      const rawHandle = resolvedHandle.replace(/^@/, '');
      const normalizedHandle = '@' + rawHandle;
      const orderItems = purchasableItems.map(i => ({
        sku: i.book.sku,
        title: i.book.title,
        qty: i.qty,
        price: i.book.final_srp,
        batch: i.book.batch,
      }));
      const regularTotal = purchasableItems.reduce((s, i) => s + i.book.final_srp * i.qty, 0);
      const orderTotal = regularTotal + (tlCartItem ? tlCartItem.price : 0);

      const orderData = {
        ref_number: orderRef,
        customer_name: customer ? customer.username : normalizedHandle,
        tiktok_handle: normalizedHandle,
        items: orderItems,
        total_price: orderTotal,
        notes: onlineNotes.trim(),
        is_pile_shipping: onlinePileShipping,
      };

      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: payload,
          metadata: {
            order_ref: orderRef,
            order_data: orderData,
            tl_request_id: tlCartItem?.request_id ?? null,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.checkout_url || !data.session_id) {
        setPaymongoError(data.error || 'Failed to create payment session. Please try again.');
        setPaymongoLoading(false);
        return;
      }

      // Store pending order + PayMongo session id — PayMongo does not inject session_id
      // into success_url, so /success reads this from localStorage to verify payment.
      const convFee = data.convenience_fee ?? 0;
      const pendingData = {
        order_ref: orderRef,
        checkout_session_id: data.session_id as string,
        tiktok_handle: normalizedHandle,
        user_slug: customer?.customerCode ?? '',
        is_new_slug: false,
        order_items: purchasableItems.map(i => ({ title: i.book.title, qty: i.qty, price: i.book.final_srp })),
        order_total: orderTotal + convFee,
        order_date: orderDate,
        order_data: orderData,
        customer_id: customer?.customerId ?? null,
        tl_request_id: tlCartItem?.request_id ?? null,
        tl_tiktok_handle: tlCartItem?.tiktok_handle ?? null,
        tl_customer_name: tlCartItem?.customer_name ?? null,
        tl_book_title: tlCartItem?.book_title ?? null,
        tl_price: tlCartItem?.price ?? null,
        tl_ref_number: tlCartItem?.ref_number ?? null,
      };
      try {
        const pendingJson = JSON.stringify(pendingData);
        localStorage.setItem('ds_pending_order', pendingJson);
        sessionStorage.setItem('ds_pending_order', pendingJson);
        localStorage.setItem('ds_checkout_session_id', data.session_id);
        sessionStorage.setItem('ds_checkout_session_id', data.session_id);
      } catch { /* ignore */ }

      // Open PayMongo in a new tab — cart stays intact until payment is confirmed
      const paymentTab = window.open(data.checkout_url, '_blank', 'noopener,noreferrer');
      if (!paymentTab) {
        setPaymongoError('Please allow pop-ups for this site to complete payment, or try again.');
      }
      setPaymongoLoading(false);
    } catch {
      setPaymongoError('Something went wrong. Please try again.');
      setPaymongoLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For guests, validate tiktok handle
    if (!customer && !form.tiktok_handle.trim()) { setError('TikTok Handle is required.'); return; }
    if (form.customer_pin.length !== 4 || !/^\d{4}$/.test(form.customer_pin)) {
      setError('PIN must be exactly 4 digits.'); return;
    }
    if (!form.payment_ref.trim()) { setError('GCash Reference Number is required.'); return; }

    // If returning guest customer (has existing slug), require them to enter it
    if (!customer && existingSlug && !form.user_slug.trim()) {
      setError('Please enter your User ID. Returning customers must provide their User ID to proceed.');
      return;
    }
    if (!customer && existingSlug && form.user_slug.trim().toUpperCase() !== existingSlug.toUpperCase()) {
      setError('User ID does not match our records. Please check and try again.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const { supabase: sb } = await import('@/lib/supabase');

      // ── Stock validation at checkout time ──────────────────
      const checkableItems = items.filter(i => !i.soldOut && isPriceVisible(i.book));
      if (checkableItems.length === 0 && !tlCartItem) {
        setError('Your cart has no purchasable titles. Titles with Price TBA cannot be ordered.');
        setSubmitting(false);
        return;
      }

      if (checkableItems.length > 0) {
        const bookIds = checkableItems.map(i => i.book.id);
        const { data: freshBooks } = await sb
          .from('books')
          .select('id, inventory, reserved, visibility, is_price_visible')
          .in('id', bookIds);

        if (freshBooks) {
          const soldOutTitles: string[] = [];
          const priceHiddenTitles: string[] = [];
          for (const item of checkableItems) {
            const fresh = freshBooks.find((b: Record<string, unknown>) => b.id === item.book.id);
            if (!fresh) continue;
            if (fresh.is_price_visible === false) {
              priceHiddenTitles.push(item.book.title);
              continue;
            }
            const isReservedSoldOut = Number(fresh.reserved) === 1;
            const isVisibilityReserved = fresh.visibility === 'Reserved';
            const available = (isReservedSoldOut || isVisibilityReserved)
              ? 0
              : Math.max(0, Number(fresh.inventory) - Number(fresh.reserved));
            if (available <= 0) {
              soldOutTitles.push(item.book.title);
              await sb.from('books').update({ visibility: 'Reserved' }).eq('id', item.book.id);
            } else if (item.qty > available) {
              setError(`"${item.book.title}" only has ${available} cop${available === 1 ? 'y' : 'ies'} available. Please update your cart.`);
              setSubmitting(false);
              return;
            }
          }
          if (priceHiddenTitles.length > 0) {
            setError(`The following title(s) no longer have a listed price: ${priceHiddenTitles.join(', ')}. Please remove them from your cart.`);
            setSubmitting(false);
            return;
          }
          if (soldOutTitles.length > 0) {
            setError(`The following title(s) are now sold out: ${soldOutTitles.join(', ')}. Please remove them from your cart.`);
            setSubmitting(false);
            return;
          }
        }
      }

      const orderRef = generateOrderRef();
      const orderDate = new Date().toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' });

      // Determine handle: logged-in customer uses their stored handle
      const resolvedHandle = customer ? customer.tiktokHandle : form.tiktok_handle.trim();
      const hashedPin = await hashPin(form.customer_pin);
      const rawHandle = resolvedHandle.replace(/^@/, '');
      const normalizedHandle = '@' + rawHandle;
      const orderItems = checkableItems.map(i => ({
        sku: i.book.sku,
        title: i.book.title,
        qty: i.qty,
        price: i.book.final_srp,
        batch: i.book.batch,
      }));

      // Include TikTok Live item in order items if present
      if (tlCartItem) {
        orderItems.push({
          sku: 'TIKTOK-LIVE',
          title: tlCartItem.book_title,
          qty: 1,
          price: tlCartItem.price,
          batch: undefined,
        } as typeof orderItems[0]);
      }

      const regularTotal = checkableItems.reduce((s, i) => s + i.book.final_srp * i.qty, 0);
      const orderTotal = Math.max(0, regularTotal - creditApplied) + (tlCartItem ? tlCartItem.price : 0);

      const { error: err } = await sb.from('orders').insert({
        ref_number: orderRef,
        customer_name: customer ? customer.username : normalizedHandle,
        tiktok_handle: normalizedHandle,
        customer_pin: hashedPin,
        items: orderItems,
        total_price: orderTotal,
        payment_method: 'GCash',
        payment_ref: form.payment_ref,
        notes: form.notes,
        status: 'Pending Payment Verification',
        is_preorder: true,
        is_pile_shipping: pileShipping,
        store_credit_applied: creditApplied > 0 ? creditApplied : 0,
        store_credit_id: creditApplied > 0 && storeCredit ? storeCredit.id : null,
      });
      if (err) throw err;

      // If TikTok Live item included, mark the TL request as Completed and link to this order
      if (tlCartItem) {
        try {
          await sb
            .from('tiktok_live_checkout_requests')
            .update({
              status: 'Completed',
              order_ref: orderRef,
              updated_at: new Date().toISOString(),
            })
            .eq('id', tlCartItem.request_id);
          // Remove from localStorage
          try { localStorage.removeItem('ds_tl_cart_item'); } catch { /* ignore */ }
        } catch { /* non-blocking */ }
      }

      // Send email notification (fire-and-forget)
      try {
        await sb.functions.invoke('notify-email', {
          body: {
            type: 'new_order',
            data: {
              ref_number: orderRef,
              tiktok_handle: normalizedHandle,
              total_price: orderTotal,
              items: orderItems,
              payment_ref: form.payment_ref,
              status: 'Pending Payment Verification',
            },
          },
        });
      } catch { /* non-blocking */ }

      // Mark store credit as used
      if (creditApplied > 0 && storeCredit) {
        await sb
          .from('store_credits')
          .update({ used_on_order_ref: orderRef, status: 'Used', is_active: false })
          .eq('id', storeCredit.id);
      }

      // ── User Slug: for logged-in customers use their customerCode; for guests generate/use existing ──
      let finalSlug = existingSlug;
      let isNewSlug = false;
      if (customer) {
        // Logged-in: use their customer code, no slug generation needed
        finalSlug = customer.customerCode;
        isNewSlug = false;
      } else if (!existingSlug) {
        // Guest new customer: generate unique slug
        let newSlug = generateUserSlug();
        let attempts = 0;
        while (attempts < 5) {
          const { data: existing } = await sb.from('customer_slugs').select('id').eq('user_slug', newSlug).maybeSingle();
          if (!existing) break;
          newSlug = generateUserSlug();
          attempts++;
        }
        await sb.from('customer_slugs').insert({ tiktok_handle: normalizedHandle, user_slug: newSlug });
        finalSlug = newSlug;
        isNewSlug = true;
      }

      clearCart();
      // Clear localStorage for this customer after successful GCash checkout
      if (customer) {
        try {
          localStorage.removeItem(`${CART_STORAGE_KEY}_${customer.customerId}`);
        } catch { /* ignore */ }
      }
      setConfirmation({
        order_ref: orderRef,
        tiktok_handle: normalizedHandle,
        user_slug: finalSlug ?? '',
        is_new_slug: isNewSlug,
        order_items: [
          ...checkableItems.map(i => ({ title: i.book.title, qty: i.qty, price: i.book.final_srp })),
          ...(tlCartItem ? [{ title: `🎬 ${tlCartItem.book_title} (TikTok Live)`, qty: 1, price: tlCartItem.price }] : []),
        ],
        order_total: orderTotal,
        order_date: orderDate,
        tl_included: !!tlCartItem,
        is_pile_shipping: pileShipping,
      });
    } catch {
      setError('Something went wrong. Please try again or message us on TikTok @daddees.shelf.');
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(10,6,2,0.97)' }}>
        <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: '#1a0e06', border: '1px solid rgba(184,134,11,0.5)', maxHeight: '90vh', overflowY: 'auto' }}>
          <div className="px-6 py-5 text-center" style={{ background: 'linear-gradient(135deg, rgba(184,134,11,0.2), rgba(139,69,19,0.15))', borderBottom: '1px solid rgba(184,134,11,0.3)' }}>
            <div className="text-4xl mb-2">✓</div>
            <h2 className="font-display text-xl font-bold" style={{ color: '#F0DFC4' }}>Order Submitted!</h2>
            <p className="text-xs mt-1" style={{ color: '#C8A45B' }}>Your order is pending payment verification</p>
          </div>
          <div className="p-6 space-y-4">
            <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(184,134,11,0.1)', border: '1px solid rgba(184,134,11,0.3)' }}>
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#C8A45B' }}>Order Reference</p>
              <p className="font-display text-2xl font-bold" style={{ color: '#F0DFC4' }}>{confirmation.order_ref}</p>
            </div>

            {/* Piling / Packing badge — highlighted for seller reference */}
            <div
              className="rounded-xl p-3 flex items-center gap-3"
              style={confirmation.is_pile_shipping
                ? { background: 'rgba(245,158,11,0.15)', border: '2px solid rgba(245,158,11,0.6)' }
                : { background: 'rgba(59,130,246,0.12)', border: '2px solid rgba(59,130,246,0.5)' }
              }
            >
              <span className="text-xl flex-shrink-0">{confirmation.is_pile_shipping ? '📦' : '🚚'}</span>
              <div>
                <p
                  className="text-xs font-bold uppercase tracking-widest"
                  style={{ color: confirmation.is_pile_shipping ? '#f59e0b' : '#60a5fa' }}
                >
                  {confirmation.is_pile_shipping ? 'FOR PILING' : 'FOR PACKING / SHIPPING'}
                </p>
                <p className="text-xs mt-0.5" style={{ color: confirmation.is_pile_shipping ? '#fcd34d' : '#93c5fd' }}>
                  {confirmation.is_pile_shipping
                    ? 'This order will be held and combined with your other active orders.' :'This order will be packed and shipped separately.'}
                </p>
              </div>
            </div>

            {/* Order Details */}
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(184,134,11,0.3)' }}>
              <div className="px-4 py-2.5" style={{ background: 'rgba(184,134,11,0.1)', borderBottom: '1px solid rgba(184,134,11,0.2)' }}>
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#C8A45B' }}>Order Details</p>
              </div>
              <div className="px-4 py-3 space-y-2">
                {confirmation.order_items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-start gap-2">
                    <span className="text-xs flex-1" style={{ color: '#D4B896' }}>{item.title} × {item.qty}</span>
                    <span className="text-xs font-bold flex-shrink-0" style={{ color: '#F0DFC4' }}>₱{(item.price * item.qty).toLocaleString()}</span>
                  </div>
                ))}
                {creditApplied > 0 && (
                  <div className="flex justify-between items-center pt-1" style={{ borderTop: '1px solid rgba(184,134,11,0.15)' }}>
                    <span className="text-xs" style={{ color: '#10b981' }}>Store Credit</span>
                    <span className="text-xs font-bold" style={{ color: '#10b981' }}>−₱{creditApplied.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-1" style={{ borderTop: '1px solid rgba(184,134,11,0.2)' }}>
                  <span className="text-xs font-bold" style={{ color: '#F0DFC4' }}>Total</span>
                  <span className="text-xs font-bold" style={{ color: '#C8A45B' }}>₱{confirmation.order_total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs" style={{ color: '#A09080' }}>Date</span>
                  <span className="text-xs" style={{ color: '#D4B896' }}>{confirmation.order_date}</span>
                </div>
              </div>
            </div>

            {/* User Slug — show for new guest slugs only */}
            {confirmation.is_new_slug && confirmation.user_slug && (
              <div className="rounded-xl p-4" style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.5)' }}>
                <p className="text-xs font-bold mb-1 flex items-center gap-1.5" style={{ color: '#a78bfa' }}>
                  🆔 Your New User ID
                </p>
                <p className="font-display text-xl font-bold text-center mb-2" style={{ color: '#F0DFC4', letterSpacing: '0.1em' }}>{confirmation.user_slug}</p>
                <p className="text-xs leading-relaxed mb-2" style={{ color: '#D4B896' }}>
                  <strong style={{ color: '#f87171' }}>⚠ Save this User ID!</strong> You will need it for all future orders. Screenshot this screen or copy it now.
                </p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(confirmation.user_slug).then(() => {
                      setSlugCopied(true);
                      setTimeout(() => setSlugCopied(false), 2000);
                    });
                  }}
                  className="w-full py-2 rounded-lg text-xs font-semibold"
                  style={{ background: 'rgba(139,92,246,0.2)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.4)' }}
                >
                  {slugCopied ? '✓ Copied!' : 'Copy User ID'}
                </button>
              </div>
            )}

            {/* TikTok screenshot instruction */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.35)' }}>
              <p className="text-xs font-bold mb-1.5 flex items-center gap-1.5" style={{ color: '#f87171' }}>
                <span>📸</span> Important Next Step
              </p>
              <p className="text-xs leading-relaxed" style={{ color: '#F0DFC4' }}>
                <strong>Screenshot this screen</strong> showing your payment reference number{confirmation.is_new_slug ? ' and User ID' : ''}, then send it to us on TikTok at{' '}
                <strong style={{ color: '#C8A45B' }}>@daddees.shelf</strong> so we can verify your payment.
              </p>
            </div>

            <p className="text-xs text-center" style={{ color: '#C8A45B' }}>
              Use your 4-digit PIN to track your order at <strong style={{ color: '#F0DFC4' }}>My Orders</strong>.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(confirmation.order_ref).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  });
                }}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold"
                style={{ background: 'rgba(184,134,11,0.2)', color: '#F0DFC4', border: '1px solid rgba(184,134,11,0.4)' }}
              >
                {copied ? '✓ Copied!' : 'Copy Ref'}
              </button>
              <button
                onClick={() => { onClose(); router.push('/orders'); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold"
                style={{ background: 'rgba(184,134,11,0.8)', color: '#1a0a00' }}
              >
                Track Order
              </button>
            </div>
            <button onClick={onClose} className="w-full text-xs py-2" style={{ color: '#A09080' }}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(10,6,2,0.97)' }}>
      <div className="relative w-full max-w-lg rounded-2xl" style={{ background: '#1a0e06', border: '1px solid rgba(184,134,11,0.4)', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 sticky top-0" style={{ background: '#1a0e06', borderBottom: '1px solid rgba(184,134,11,0.25)', zIndex: 1 }}>
          <h2 className="font-display text-lg font-bold" style={{ color: '#F0DFC4' }}>Checkout</h2>
          <button onClick={onClose} className="p-1 rounded-lg" style={{ color: '#C8A45B' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        {/* Logged-in customer greeting */}
        {customer && (
          <div className="px-6 py-3" style={{ background: 'rgba(139,92,246,0.08)', borderBottom: '1px solid rgba(139,92,246,0.2)' }}>
            <p className="text-xs font-semibold" style={{ color: '#a78bfa' }}>
              ✦ Checking out as <strong style={{ color: '#F0DFC4' }}>{customer.username}</strong> ({customer.tiktokHandle})
            </p>
          </div>
        )}

        {/* Cart summary */}
        <div className="px-6 py-4" style={{ borderBottom: '1px solid rgba(184,134,11,0.2)', background: 'rgba(184,134,11,0.04)' }}>
          <p className="text-xs font-semibold mb-2" style={{ color: '#C8A45B' }}>
            Your Cart ({items.filter(i => !i.soldOut && isPriceVisible(i.book)).length + (tlCartItem ? 1 : 0)}{' '}
            {(items.filter(i => !i.soldOut && isPriceVisible(i.book)).length + (tlCartItem ? 1 : 0)) === 1 ? 'title' : 'titles'})
          </p>
          {items.filter(i => !i.soldOut && isPriceVisible(i.book)).map((item, i) => (
            <div key={i} className="flex justify-between text-xs mb-1">
              <span style={{ color: '#D4B896' }}>{item.book.title} × {item.qty}</span>
              <span style={{ color: '#F0DFC4', fontWeight: 600 }}>₱{(item.book.final_srp * item.qty).toLocaleString()}</span>
            </div>
          ))}
          {/* TikTok Live item in summary */}
          {tlCartItem && (
            <div className="flex justify-between text-xs mb-1 mt-1 pt-1" style={{ borderTop: items.filter(i => !i.soldOut && isPriceVisible(i.book)).length > 0 ? '1px dashed rgba(239,68,68,0.3)' : 'none' }}>
              <span style={{ color: '#fca5a5' }}>🎬 {tlCartItem.book_title} (TikTok Live) × 1</span>
              <span style={{ color: '#fca5a5', fontWeight: 600 }}>₱{tlCartItem.price.toLocaleString()}</span>
            </div>
          )}
          {/* Store credit deduction row */}
          {creditApplied > 0 && (
            <div className="flex justify-between text-xs mt-1">
              <span style={{ color: '#10b981' }}>Store Credit Applied</span>
              <span style={{ color: '#10b981', fontWeight: 600 }}>−₱{creditApplied.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-bold mt-2 pt-2" style={{ borderTop: '1px solid rgba(184,134,11,0.2)' }}>
            <span style={{ color: '#F0DFC4' }}>Total</span>
            <span style={{ color: '#C8A45B' }}>₱{total.toLocaleString()}</span>
          </div>
          {/* Convenience fee preview for card payments */}
          {paymentMethod === 'paymongo' && (() => {
            const subtotal = items.filter(i => !i.soldOut && isPriceVisible(i.book)).reduce((s, i) => s + i.book.final_srp * i.qty, 0) + (tlCartItem ? tlCartItem.price : 0);
            const fee = Math.round(subtotal * 0.045 * 100) / 100 + 15;
            return (
              <>
                <div className="flex justify-between text-xs mt-1.5">
                  <span style={{ color: '#93c5fd' }}>Convenience Fee</span>
                  <span style={{ color: '#93c5fd', fontWeight: 600 }}>+₱{fee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>* Convenience fee applies to card payments only.</p>
                <div className="flex justify-between text-sm font-bold mt-1.5 pt-1.5" style={{ borderTop: '1px solid rgba(59,130,246,0.2)' }}>
                  <span style={{ color: '#F0DFC4' }}>Total Charged</span>
                  <span style={{ color: '#93c5fd' }}>₱{(subtotal + fee).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </>
            );
          })()}
        </div>

        {/* Payment Method Selector */}
        <div className="px-6 pt-5 pb-2">
          <p className="text-xs font-semibold mb-2" style={{ color: '#C8A45B' }}>Choose Payment Method</p>
          <div className={`grid gap-2 ${onlinePaymentEnabled ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <button
              type="button"
              onClick={() => setPaymentMethod('gcash')}
              className="py-2.5 px-3 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all"
              style={{
                background: paymentMethod === 'gcash' ? 'rgba(16,185,129,0.18)' : 'rgba(16,185,129,0.06)',
                color: '#10b981',
                border: `1px solid ${paymentMethod === 'gcash' ? 'rgba(16,185,129,0.6)' : 'rgba(16,185,129,0.25)'}`,
              }}
            >
              💚 GCash / Manual
            </button>
            {onlinePaymentEnabled && (
              <button
                type="button"
                onClick={() => setPaymentMethod('paymongo')}
                className="py-2.5 px-3 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all"
                style={{
                  background: paymentMethod === 'paymongo' ? 'rgba(59,130,246,0.18)' : 'rgba(59,130,246,0.06)',
                  color: '#93c5fd',
                  border: `1px solid ${paymentMethod === 'paymongo' ? 'rgba(59,130,246,0.6)' : 'rgba(59,130,246,0.25)'}`,
                }}
              >
                💳 Card / Credit Card
              </button>
            )}
          </div>
        </div>

        {/* PayMongo Option */}
        {paymentMethod === 'paymongo' && (
          <div className="px-6 pb-5 pt-2 space-y-3">
            <div className="rounded-xl p-4" style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.25)' }}>
              <p className="text-xs font-semibold mb-1" style={{ color: '#93c5fd' }}>💳 Secure Online Checkout</p>
              <p className="text-xs leading-relaxed" style={{ color: '#D4B896' }}>
                You will be redirected to a secure checkout page to pay using your card or credit card. Your order will be confirmed automatically after payment.
              </p>
            </div>

            {/* Pile / Bundle Shipping */}
            <label
              className="flex items-start gap-3 rounded-xl p-3 cursor-pointer"
              style={{ background: onlinePileShipping ? 'rgba(200,164,91,0.12)' : 'rgba(184,134,11,0.04)', border: `1px solid ${onlinePileShipping ? 'rgba(200,164,91,0.45)' : 'rgba(184,134,11,0.2)'}` }}
            >
              <input
                type="checkbox"
                checked={onlinePileShipping}
                onChange={e => setOnlinePileShipping(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded flex-shrink-0"
                style={{ accentColor: '#C8A45B' }}
              />
              <span>
                <span className="block text-sm font-semibold" style={{ color: '#F0DFC4' }}>Pile with existing orders.</span>
                <span className="block text-xs mt-1" style={{ color: '#A09080', lineHeight: 1.5 }}>
                  Hold this order and combine it with your other active unshipped orders so they ship together as one parcel.
                </span>
              </span>
            </label>

            {/* Notes */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#C8A45B' }}>
                Notes <span className="text-xs font-normal" style={{ color: '#A09080' }}>(optional)</span>
              </label>
              <textarea
                rows={2}
                value={onlineNotes}
                onChange={e => setOnlineNotes(e.target.value)}
                className="input-field text-sm resize-none"
                placeholder="Any special instructions..."
              />
            </div>

            {paymongoError && (
              <p className="text-xs" style={{ color: '#f87171' }}>{paymongoError}</p>
            )}
            {/* Shipping fee disclaimer */}
            <div className="rounded-xl p-3" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <p className="text-xs" style={{ color: '#f87171' }}>
                <strong>📦 Shipping fee is shouldered by the buyer.</strong>
              </p>
            </div>
            <button
              type="button"
              onClick={handlePayMongo}
              disabled={paymongoLoading}
              className="w-full py-3 text-sm font-semibold rounded-xl flex items-center justify-center gap-2"
              style={{
                background: paymongoLoading ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.22)',
                color: '#93c5fd',
                border: '1px solid rgba(59,130,246,0.5)',
                opacity: paymongoLoading ? 0.7 : 1,
              }}
            >
              {paymongoLoading ? (
                <>
                  <span className="inline-block w-3.5 h-3.5 rounded-full border border-t-transparent animate-spin" style={{ borderColor: '#93c5fd' }} />
              Redirecting to secure checkout…
                </>
              ) : (
                <>💳 Pay Now via Card / Credit Card</>
              )}
            </button>
          </div>
        )}

        {/* GCash Manual Form */}
        {paymentMethod === 'gcash' && (
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* TikTok Handle — only for guests */}
          {!customer && (
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#C8A45B' }}>
                TikTok Handle <span style={{ color: '#f59e0b' }}>*</span>
              </label>
              <input
                type="text"
                required
                value={form.tiktok_handle}
                onChange={e => setForm(f => ({ ...f, tiktok_handle: e.target.value }))}
                className="input-field text-sm"
                placeholder="@yourtiktok"
              />
              <p className="text-xs mt-1" style={{ color: '#A09080' }}>You must be a follower of @daddees.shelf to place preorders.</p>

              {/* Slug check indicator */}
              {form.tiktok_handle.trim() && (
                <div className="mt-2">
                  {slugChecking ? (
                    <p className="text-xs flex items-center gap-1.5" style={{ color: '#A09080' }}>
                      <span className="inline-block w-3 h-3 rounded-full border border-t-transparent animate-spin" style={{ borderColor: '#A09080' }} />
                      Checking account…
                    </p>
                  ) : existingSlug ? (
                    <div className="rounded-lg px-3 py-2" style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.35)' }}>
                      <p className="text-xs font-semibold" style={{ color: '#a78bfa' }}>
                        ✦ Returning customer detected. Please enter your User ID below.
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs" style={{ color: '#10b981' }}>✦ New customer — a User ID will be assigned after your order.</p>
                  )}
                </div>
              )}

              {/* Store credit status indicator */}
              {form.tiktok_handle.trim() && (
                <div className="mt-2">
                  {creditLoading ? (
                    <p className="text-xs flex items-center gap-1.5" style={{ color: '#A09080' }}>
                      <span className="inline-block w-3 h-3 rounded-full border border-t-transparent animate-spin" style={{ borderColor: '#A09080' }} />
                      Checking for store credit…
                    </p>
                  ) : storeCredit ? (
                    <div className="rounded-lg px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.35)' }}>
                      <span style={{ color: '#10b981' }}>✦</span>
                      <p className="text-xs font-semibold" style={{ color: '#10b981' }}>
                        Store credit found: <span className="font-bold">₱{storeCredit.amount.toLocaleString()}</span> will be deducted from your total.
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs" style={{ color: '#A09080' }}>No active store credit found for this handle.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Store credit indicator for logged-in customers */}
          {customer && (
            <div className="mt-1">
              {creditLoading ? (
                <p className="text-xs flex items-center gap-1.5" style={{ color: '#A09080' }}>
                  <span className="inline-block w-3 h-3 rounded-full border border-t-transparent animate-spin" style={{ borderColor: '#A09080' }} />
                  Checking for store credit…
                </p>
              ) : storeCredit ? (
                <div className="rounded-lg px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.35)' }}>
                  <span style={{ color: '#10b981' }}>✦</span>
                  <p className="text-xs font-semibold" style={{ color: '#10b981' }}>
                    Store credit found: <span className="font-bold">₱{storeCredit.amount.toLocaleString()}</span> will be deducted from your total.
                  </p>
                </div>
              ) : null}
            </div>
          )}

          {/* User Slug — required for returning guest customers only */}
          {!customer && existingSlug && (
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#C8A45B' }}>
                User ID <span style={{ color: '#f59e0b' }}>*</span>
              </label>
              <input
                type="text"
                required
                value={form.user_slug}
                onChange={e => setForm(f => ({ ...f, user_slug: e.target.value.toUpperCase() }))}
                className="input-field text-sm text-center tracking-widest"
                placeholder="DS-XXXXXXXX"
                style={{ fontFamily: 'monospace' }}
              />
              <p className="text-xs mt-1" style={{ color: '#A09080' }}>Enter the User ID you received on your first order.</p>
            </div>
          )}

          {/* PIN */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#C8A45B' }}>
              4-Digit PIN <span style={{ color: '#f59e0b' }}>*</span>
            </label>
            <input
              type="password"
              required
              maxLength={4}
              value={form.customer_pin}
              onChange={e => setForm(f => ({ ...f, customer_pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
              className="input-field text-sm text-center tracking-widest"
              placeholder="••••"
              inputMode="numeric"
            />
            <p className="text-xs mt-1" style={{ color: '#A09080' }}>
              {customer ? 'Enter your account PIN to confirm this order.' : 'Remember this PIN — you will use it to track your order in My Orders.'}
            </p>
          </div>

          {/* GCash QR */}
          <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)' }}>
            <p className="text-xs font-bold mb-2" style={{ color: '#10b981' }}>✦ GCash Payment Instructions</p>
            <div className="relative w-44 h-44 mx-auto mb-3">
              <button
                type="button"
                onClick={() => setQrExpanded(true)}
                className="w-full h-full rounded-xl overflow-hidden block relative"
                style={{ border: '2px solid rgba(16,185,129,0.4)', cursor: 'zoom-in', padding: 0, background: 'none' }}
                title="Tap to expand QR code"
                aria-label="Expand GCash QR code"
              >
                <AppImage
                  src="/assets/images/36c6a594-8ce5-4d14-8600-0e7b65f58ff0-1786006380177.jpg"
                  alt="GCash QR code for Daddee's Shelf payment"
                  width={176}
                  height={176}
                  className="w-full h-full object-cover"
                />
              </button>
              {/* Tap hint — always visible on mobile, hover on desktop */}
              <div
                className="absolute bottom-0 left-0 right-0 flex items-center justify-center py-1 rounded-b-xl pointer-events-none"
                style={{ background: 'rgba(0,0,0,0.55)' }}
              >
                <span className="text-white text-xs font-semibold">🔍 Tap to expand</span>
              </div>
            </div>
            <ol className="text-xs text-left space-y-1.5 max-w-xs mx-auto" style={{ color: '#D4B896' }}>
              <li className="flex gap-2"><span className="font-bold" style={{ color: '#10b981' }}>1.</span> Scan the QR code with your GCash app</li>
              <li className="flex gap-2"><span className="font-bold" style={{ color: '#10b981' }}>2.</span> Send the exact total amount</li>
              <li className="flex gap-2"><span className="font-bold" style={{ color: '#10b981' }}>3.</span> Copy your GCash Reference Number</li>
              <li className="flex gap-2"><span className="font-bold" style={{ color: '#10b981' }}>4.</span> Paste it in the field below</li>
            </ol>
          </div>

          {/* Payment Reference */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#C8A45B' }}>
              GCash Reference Number <span style={{ color: '#f59e0b' }}>*</span>
            </label>
            <input
              type="text"
              required
              value={form.payment_ref}
              onChange={e => setForm(f => ({ ...f, payment_ref: e.target.value }))}
              className="input-field text-sm"
              placeholder="e.g. 1234567890"
            />
            <p className="text-xs mt-1" style={{ color: '#A09080' }}>Copy the reference number from your GCash transaction receipt.</p>
          </div>

          {/* Pile / Bundle Shipping */}
          <label
            className="flex items-start gap-3 rounded-xl p-3 cursor-pointer"
            style={{ background: pileShipping ? 'rgba(200,164,91,0.12)' : 'rgba(184,134,11,0.04)', border: `1px solid ${pileShipping ? 'rgba(200,164,91,0.45)' : 'rgba(184,134,11,0.2)'}` }}
          >
            <input
              type="checkbox"
              checked={pileShipping}
              onChange={e => setPileShipping(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded flex-shrink-0"
              style={{ accentColor: '#C8A45B' }}
            />
            <span>
              <span className="block text-sm font-semibold" style={{ color: '#F0DFC4' }}>Pile with existing orders.</span>
              <span className="block text-xs mt-1" style={{ color: '#A09080', lineHeight: 1.5 }}>
                Hold this order and combine it with your other active unshipped orders so they ship together as one parcel.
              </span>
            </span>
          </label>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#C8A45B' }}>
              Notes <span className="text-xs font-normal" style={{ color: '#A09080' }}>(optional)</span>
            </label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="input-field text-sm resize-none"
              placeholder="Any special instructions..."
            />
          </div>

          <div className="rounded-xl p-3" style={{ background: 'rgba(184,134,11,0.06)', border: '1px solid rgba(184,134,11,0.2)' }}>
            <p className="text-xs" style={{ color: '#D4B896' }}>
              <strong style={{ color: '#F0DFC4' }}>Note:</strong> Shipping details will be collected after your books arrive. Supported couriers: J&T Express (Nationwide) and Lalamove (Metro Manila / Nearby Areas).
            </p>
          </div>

          {/* Shipping fee disclaimer */}
          <div className="rounded-xl p-3" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <p className="text-xs" style={{ color: '#f87171' }}>
              <strong>📦 Shipping fee is shouldered by the buyer.</strong>
            </p>
          </div>

          {error && <p className="text-sm" style={{ color: '#f87171' }}>{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full py-3 text-sm"
            style={{ opacity: submitting ? 0.7 : 1 }}
          >
            {submitting ? 'Submitting...' : 'Submit Order ✦'}
          </button>
        </form>
        )}
      </div>

      {/* QR Code expanded overlay */}
      {qrExpanded && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.95)' }}
          onClick={() => setQrExpanded(false)}
        >
          <div className="relative" onClick={e => e.stopPropagation()}>
            <div className="rounded-2xl overflow-hidden" style={{ border: '3px solid rgba(16,185,129,0.5)', boxShadow: '0 0 60px rgba(16,185,129,0.2)' }}>
              <AppImage
                src="/assets/images/36c6a594-8ce5-4d14-8600-0e7b65f58ff0-1786006380177.jpg"
                alt="GCash QR code for Daddee's Shelf payment — expanded view"
                width={320}
                height={320}
                className="block"
                style={{ maxWidth: '80vw', maxHeight: '80vh', objectFit: 'contain' }}
              />
            </div>
            <button
              onClick={() => setQrExpanded(false)}
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
              style={{ background: '#1a0e06', border: '2px solid rgba(16,185,129,0.5)', color: '#10b981' }}
            >
              ✕
            </button>
            <p className="text-xs text-center mt-3" style={{ color: 'rgba(255,255,255,0.5)' }}>Tap outside or ✕ to close</p>
          </div>
        </div>
      )}
    </div>
  );
}